// Bilingual copy for the Bizsprite marketing site.
// Outline source: 《Bizsprite 官网产品页面内容与排版建议》(2026-10-08).
// English is the default; Chinese mirrors the same outline.

export const landingContent = {
  en: {
    langLabel: '中文',
    nav: {
      home: 'Home',
      products: 'Products',
      pricing: 'Pricing',
      cases: 'Call Demos',
      resources: 'Resources',
      about: 'About Us',
      blog: 'Insights',
      contact: 'Contact Us',
      openAccount: 'Get Started',
    },

    // ---------- Hero ----------
    hero: {
      badge: 'Built for cross-border businesses',
      titleLine1: 'Let AI connect',
      titleBold1: 'every customer conversation,',
      titleLine2: 'Serve better,',
      titleBold2: 'grow faster.',
      subtitle:
        'With a dedicated North American number and AI voice reception, every incoming call is answered automatically — questions resolved, customer needs identified, and follow-up carried forward, so service stays timely and connected.',
      ctaPrimary: 'Explore solutions | Experience AI service',
      stats: [
        { value: '24/7', label: 'Always available to answer' },
        { value: 'US', label: 'Local North American number' },
        { value: 'Call → SMS → Ticket', label: 'One connected workflow' },
      ],
      // 首页 hero 右侧：四张卡轮播，每张 5 秒（原来是「听 AI 怎么处理一通来电」播放卡）。
      // 开通流程 / 部署 由上线指南五步拆成两段；价格直接复用下方 pricing 块，不重复维护文案。
      carousel: {
        regionLabel: 'Homepage highlights',
        goTo: 'Go to slide',
      },
      slides: [
        {
          key: 'launch',
          tag: 'Activation',
          title: 'From payment to a live number, in four steps',
          subtitle: 'Provisioning runs through our partner, OneSuite Business — the path is fixed.',
          steps: [
            { title: 'Create a OneSuite Business account', text: 'Company name, contact, business email and address. The email code is valid for 15 minutes.' },
            { title: 'Choose a plan and pay', text: 'Once payment succeeds, the OneSuite admin account and the Bizsprite AI platform account are created automatically.' },
            { title: 'Top up the account', text: 'The account is prepaid. Top up once, or turn on auto-recharge so reception never stops on a low balance.' },
            { title: 'The number goes live', text: 'With materials ready, the local U.S. number is typically ready in about 3–7 business days.' },
          ],
        },
        {
          key: 'deploy',
          tag: 'Deployment',
          title: 'Complete materials are what make the answers accurate',
          subtitle: 'This step decides answer quality — the AI answers only within the materials you give it.',
          steps: [
            { title: 'Create the AI agent', text: 'Create it under AI Assistants and fill in the basic settings.' },
            { title: 'Upload product and FAQ materials', text: 'Use the provided templates for the product list, SKUs, compatible models, shipping and after-sales policies.' },
            { title: 'Configure the service rules', text: 'Set the greeting, service hours and human-handover rules.' },
            { title: 'Test before going live', text: 'Test reception, answers and handover with questions your customers have actually asked.' },
          ],
        },
        {
          key: 'price',
          tag: 'Pricing',
          title: 'One monthly fee, unlimited AI reception',
          subtitle: 'The subscription covers AI reception and a local U.S. number; call minutes and SMS are billed by usage.',
        },
        {
          key: 'guarantee',
          tag: 'Peace of mind',
          title: 'Run it a month — not satisfied, full refund',
          subtitle: 'The answers you need before deciding.',
          items: [
            { icon: 'shield', title: 'First month, full refund', text: 'Pay the first month. If you are not satisfied within 30 days, request a full refund with no handling fees.' },
            { icon: 'clock', title: 'Live in about 3–7 business days', text: 'Once materials are ready the number application can be submitted; final timing follows carrier review.' },
            { icon: 'headset', title: 'Support by email and phone', text: 'Support email is answered within one business day. For anything urgent, call us and settle it on the spot.' },
          ],
          cta: 'View pricing',
        },
      ],
    },

    // ---------- Home flow (touchpoints + map + SMS) ----------
    homeFlow: {
      eyebrow: 'One brand number, every touchpoint',
      title: 'One brand number, every customer conversation connected',
      subtitle: 'Calls from your website, product packaging and brand service pages are all answered by one U.S. number, giving customers a consistent, continuous experience.',
      sources: [
        { name: 'Brand website', text: 'Phone contact entry' },
        { name: 'Product packaging', text: 'After-sales contact' },
        { name: 'Brand service page', text: 'Brand phone number' },
      ],
      mapBadge: 'Inbound from North America',
      mapCalloutTitle: 'Your U.S. number',
      mapCalloutSub: 'AI answers from your materials',
      nextStep1Title: 'Answer the question clearly',
      nextStep1Text: 'AI answers from your product materials; if anything is unclear it asks one more question before sending a link.',
      nextStep2Title: 'Hand the next step to the customer',
      nextStep2Text: 'Product links, materials collection, and tickets are sent in the right order—your team picks up the next morning.',
      flow1Label: 'Product selection',
      flow1Title: 'Confirm the product on the call, send the link to the customer',
      flow1Sub: 'After the customer agrees, send the matching product link by SMS.',
      flow1Steps: ['Confirm', 'Agree', 'SMS link'],
      flow2Label: 'Unresolved issues',
      flow2Title: 'Send a ticket link, gather materials, the team picks it up',
      flow2Sub: 'Explain next steps and get consent before sending the materials-collection link.',
      flow2Steps: ['Pre-ticket', 'SMS entry', 'Materials', 'Formal ticket', 'Team follows up'],
    },

    // ---------- Home teaser (product + cases preview) ----------
    homeTeaser: {
      productEyebrow: 'Our products',
      productTitle: 'A number, an AI. Start with the one that fits.',
      productSubtitle: 'From a number alone to number + AI, two configurations for two stages of business.',
      productBadge: 'RECOMMENDED',
      aiFeatures: [
        '24/7 auto-answer, covers nights and weekends',
        'Answers from your product materials',
        'Sends links during call, complex issues become tickets',
        'Recording, summary, and ticket handed off together',
      ],
      standaloneCta: 'Learn about the product',
      standaloneCtaAlt: 'Listen to a call demo',
      flowTitle: 'How one call is handled',
      flowSteps: ['Answer', 'Reply', 'Link', 'Ticket'],
      flowNote: 'Every call goes through these steps—nothing slips, nothing gets lost in handoff.',
      casesEyebrow: 'Real calls',
      casesTitle: 'See how AI handles your real calls',
      casesSubtitle: 'Three of the most common cross-border scenarios. Tap one and the actual call plays right here.',
      caseLink: 'Listen to a call demo',
      casesMoreCta: 'See all call demos',
    },

    // ---------- Home value cards (4 cards under the hero) ----------
    valueCards: {
      eyebrow: 'Core value',
      title: 'Meet customer needs — faster, more connected service',
      subtitle: 'AI voice reception fits into the daily workflow. From the moment a customer calls to the moment information is processed, it lifts both response speed and service quality.',
      items: [
        {
          title: 'Round-the-clock response to incoming calls',
          text: 'The AI answers calls that arrive outside business hours, logs what the customer needs, and syncs the key details to your team so follow-up starts without a gap.',
        },
        {
          title: 'Common questions handled automatically',
          text: 'Drawing on your product materials and knowledge base, the AI answers questions about models, compatibility and sizing — cutting repeat conversations and freeing the team for the customers who need them.',
        },
        {
          title: 'After-sales information managed in one place',
          text: 'During the call the AI texts the relevant links, then creates a service ticket from what the customer submits — less re-collecting, less back-and-forth.',
        },
        {
          title: 'Continuously improving answer quality',
          text: 'Review AI replies by question type. Every change is logged with its content and timestamp, so the team can correct mistakes quickly and keep answers accurate.',
        },
      ],
    },

    // ---------- Pricing ----------
    pricing: {
      eyebrow: 'Pricing',
      titleLine1: 'Flexible, transparent pricing',
      // 原来是「with no usage limits」——但通话分钟、短信本身按用量计费，
      // 说「完全不限用量」与下方费用说明自相矛盾，改为只对 AI 接听不加限制。
      titleLine2: 'that fits every stage of your business',
      subtitle:
        'Choose the AI voice service plan that matches your call volume and service scale, and manage customer service costs with a clear fee structure.',
      planLabel: 'Monthly subscription · Starter',
      price: '$66',
      perMonth: '/month',
      wasPrice: '$99',
      priceNote: '* Introductory price; returns to the standard $99 rate',
      features: [
        'AI call answering · AI customer conversation',
        '30+ natural voices · Local U.S. number',
        'Product knowledge base Q&A · No usage limits',
      ],
      refundTitle: 'Not satisfied in the first month? Full refund',
      refundText:
        'Pay the first month\u2019s fee; if you are not satisfied within 30 days, you can request a full refund with no handling fees.',
      contactSales: 'Contact us',
      getStarted: 'Get Started',
      assurances: [
        'Monthly subscription · No hidden fees',
        'Activated in about 3\u20137 business days once materials are ready',
        'Local U.S. number · Area codes available',
      ],
      // 四张卡是上面费用明细表的概括：表里 8 个分组归成这 4 类
      otherCostsTitle: 'What the fees come down to',
      otherCostsText:
        'Understand how each service is billed and what conditions apply, so you can plan day-to-day operating costs.',
      otherCosts: [
        { icon: 'FiPhone', title: 'Numbers & activation', text: 'One-time activation plus a monthly retention fee. Main line, trunk lines and extensions are billed separately.' },
        { icon: 'FiMic', title: 'Voice calls', text: 'Inbound at $0.015/min, outbound at $0.025/min — billed by actual call minutes.' },
        { icon: 'FiMessageSquare', title: 'SMS', text: 'One-time activation plus a monthly retention fee, and per-message send/receive. This is the channel the AI uses to text product links.' },
        { icon: 'FiZap', title: 'AI services', text: 'Real-time voice AI per minute, dialogue and data by usage, recording and knowledge base by volume, plus the platform and AI subscription billed monthly.' },
      ],
      faqsTitle: 'Common questions about product pricing',
      faqsSubtitle: 'Understand subscription fees, service billing, usage scope and plan adjustments.',
      faqs: [
        { q: 'Is $66 the permanent price?', a: '$66 is the introductory monthly fee. It returns to $99/month once the launch offer ends.' },
        { q: 'Is there an overage trap?', a: 'AI answering and AI conversations themselves are unlimited. Call minutes and SMS are metered by the telecom provider, and every unit price is listed on this page.' },
        { q: 'Is there a discount for annual payment?', a: 'Enterprise, channel and annual customers may have different terms — contact sales to confirm the exact discount.' },
        { q: 'Can I cancel at any time?', a: 'Cancellation and refunds depend on your plan, billing cycle and any telecom resources already consumed. We will walk you through it.' },
      ],

      // ---- Fee breakdown (screen 2) ----
      feeBreakdown: {
        eyebrow: 'Fee Breakdown',
        title: 'Pay for what you use, with costs you can control',
        subtitle:
          'Configure the plan that fits your business model, usage scenarios and service needs.',
        col: { item: 'Billing Item', unit: 'Unit', price: 'Unit Price (USD)', note: 'Remarks' },
        notBilled: 'Not billed separately',
        tbd: 'TBD',
        sections: [
          {
            label: 'Telecom Services',
            groups: [
              {
                label: 'Number & Activation',
                items: [
                  { item: 'NA Business Phone Activation', unit: 'One-time', price: '$15.00', note: '1 main line + 2 extensions, 1 default AI agent' },
                  { item: 'Trunk Line Addition', unit: 'One-time per line', price: '$15.00', note: 'Add trunk line; free after 3 months' },
                  { item: 'Trunk Line Retention', unit: 'Per line/month', price: '$10.00', note: 'Discount conditions TBD' },
                  { item: 'Extension Addition', unit: 'One-time per ext', price: '$10.00', note: '—' },
                  { item: 'Extension Retention', unit: 'Per ext/month', price: '—', note: '—' },
                  { item: 'Add\u2019l Trunk Activation', unit: 'Per month', price: '$10.00', note: 'Free after 3 months' },
                  { item: 'Add\u2019l Extension License', unit: 'Per month', price: '$10.00', note: '—' },
                ],
              },
              {
                label: 'Voice',
                items: [
                  { item: 'Inbound Call', unit: 'Per minute', price: '$0.015', note: 'Inbound caller ID' },
                  { item: 'Outbound Call', unit: 'Per minute', price: '$0.025', note: 'Outbound caller ID' },
                ],
              },
              {
                label: 'SMS',
                items: [
                  { item: 'SMS Activation', unit: 'One-time', price: '$15.00', note: 'Enterprise number SMS service' },
                  { item: 'SMS Retention', unit: 'Per month', price: '$5.00', note: 'Discount conditions TBD' },
                  { item: 'SMS Receiving', unit: 'Per message', price: '$0.015', note: 'Receive customer SMS, forward to enterprise email' },
                  { item: 'SMS Push', unit: 'Per message', price: '$0.03', note: 'Template SMS via public SMS platform' },
                ],
              },
            ],
          },
          {
            label: 'AI Services',
            groups: [
              {
                label: 'Smart Voice',
                items: [
                  { item: 'Real-time Voice AI', unit: 'Per minute', price: '$0.085', note: 'Real-time AI voice dialogue' },
                  { item: 'LLM Inference', unit: '—', price: '$0', note: '{{notBilled}}' },
                  { item: 'Telephony', unit: 'Per minute', price: '$0', note: '{{notBilled}}' },
                ],
              },
              {
                label: 'Dialogue & Data',
                items: [
                  { item: 'Conversation Text Record', unit: 'Per 1K chars', price: '$0.003', note: 'Call transcription' },
                  { item: 'Conversation Intelligence', unit: 'Per 1K chars', price: '$0', note: '{{notBilled}}' },
                  { item: 'Customer Profile', unit: 'Per profile/day', price: '$0', note: '{{notBilled}}' },
                  { item: 'Customer Memory', unit: 'Per instance', price: '$0.0035', note: '—' },
                  { item: 'Knowledge Base', unit: 'Per instance', price: '$0', note: '{{notBilled}}' },
                  { item: 'Knowledge Base', unit: 'Per GB/month', price: '$0', note: '{{notBilled}}' },
                ],
              },
              {
                label: 'Recording & Storage',
                items: [
                  { item: 'Recording & Transcription', unit: '—', price: '$0.0001', note: 'Call recording and text archival' },
                ],
              },
              {
                label: 'Platform & Subscription',
                items: [
                  { item: 'SMS (AI portion)', unit: 'Per message', price: '{{tbd}}', note: 'Fee allocation TBD' },
                  { item: 'Platform Fixed Fee', unit: '—', price: '{{tbd}}', note: 'Internal cost item, price TBD' },
                  { item: 'AI Subscription', unit: 'Monthly', price: '{{tbd}}', note: 'Internal cost item, price TBD' },
                ],
              },
            ],
          },
        ],
      },
    },

    // ---------- Case demos hero ----------
    casesHero: {
      eyebrow: 'Call demos',
      title: 'Listen to real conversations,',
      titleBold: 'see how AI voice performs',
      subtitle: 'Through call examples from different business scenarios, see how the AI identifies customer needs, holds a natural conversation, and helps collect information and respond.',
      ctaPrimary: 'View product demo',
      stats: [
        { value: '7×24', label: 'Always-on AI pickup' },
        { value: '4', label: 'Typical seller scenarios' },
        { value: 'Real', label: 'call recordings' },
      ],
    },

    // ---------- Two products ----------
    products: {
      eyebrow: 'Our products',
      title: 'AI customer service solutions,',
      titleBold: 'built for different business needs',
      subtitle:
        'From AI voice reception to workflow coordination, we help businesses build a more efficient and flexible customer service system.',
      // 「立即开通」会跳出本站 —— 只保留按钮下方这一行。
      // 措辞去掉了原来「That redirect is the normal path, not a wrong turn」的防御性说法，
      // 越强调「不是走错站」越像在解释，改成中性陈述即可。
      partnerInline:
        '"Get started" opens the OneSuite Business registration page, where the U.S. number and the telecom account are created.',
      // 暂时未使用：原产品页卡片区下方那条整条说明卡已下架（原因见 LandingPage.js 里的注释）。
      // 完整解释在「关于我们 → 合作方」那一屏。要恢复产品页的卡片，把这段接回 JSX 即可。
      partnerNote: {
        title: 'Why "Get started" leaves this site',
        text: 'Bizsprite supplies the AI. OneSuite Business supplies the U.S. number and the telecom service behind it — the number, the call minutes, the SMS channel and the billing. One service, two accounts, and the jump between them is the normal path.',
        cta: 'Visit OneSuite Business',
      },
      // 痛点：模板里是独立的一行四张卡，不再包在深色引言面板里，所以这里只留 items
      painPoints: {
        items: [
          {
            icon: 'moon',
            title: 'After-hours calls get missed',
            text: 'A U.S. customer calls at night, your team is offline, and the call goes to voicemail—or to a competitor.',
          },
          {
            icon: 'repeat',
            title: 'Same questions, all day long',
            text: 'Compatibility, sizing, returns—your team answers the same ten questions over and over.',
          },
          {
            icon: 'clipboard',
            title: 'After-sales info collected piecemeal',
            text: 'Photos, order numbers, problem descriptions arrive across calls, messages and emails. Hard to assemble.',
          },
          {
            icon: 'globe',
            title: 'No U.S. number feels off-brand',
            text: 'Customers see a personal cell or a foreign area code and wonder whether you are a real business.',
          },
        ],
      },
      choice: {
        solvesLabel: 'What it solves',
        featuresLabel: 'What is included',
        aiBadge: 'RECOMMENDED',
      },
      cards: [
        {
          kind: 'number',
          name: 'Standalone Number',
          tagline: 'A dedicated contact point that keeps brand communication consistent',
          solves: ['The brand problem of having no U.S. number', 'Customer trust that you are a real business'],
          features: ['A local U.S. business number', 'Forwards to any device or line you choose', 'Standard voice service'],
          note: 'For sellers who answer their own calls and only need a proper U.S. number.',
          aside: { label: 'On demand', text: 'Depends on the number plan' },
          cta: 'Learn about the product',
        },
        {
          kind: 'ai',
          name: 'Number + AI Voice Agent',
          tagline: 'Answers customer calls intelligently, lifting service efficiency',
          solves: [
            'No after-hours call slips through',
            'Repetitive questions answered in one pass',
            'After-sales details gathered into a ticket',
          ],
          features: [
            'A local U.S. number (area codes available while in stock)',
            '24/7 AI answering and replies',
            'SMS links and after-sales tickets',
            'Recording, transcript and summary',
          ],
          note: 'Built for small and mid-size cross-border sellers with high call volume that need automatic reception and a complete record.',
          price: { amount: '$66', unit: '/mo', fine: '(Was $99 · full refund in the first month)' },
          cta: 'Get started',
          ctaAlt: 'Listen to a call demo',
        },
      ],
      flowTitle: 'From incoming call to follow-up, one connected process',
      flowSubtitle: 'From answering to follow-up, information from each step stays in one flow, reducing repeated conversations and missed details so service handover runs more smoothly.',
      flowStepLabel: 'Step',
      flow: [
        { icon: 'phone', title: 'Customer rings the number', text: 'From any touchpoint where you place the number.' },
        { icon: 'headphones', title: 'AI picks up and listens', text: 'Recognizes the need—order, compatibility, after-sales.' },
        { icon: 'message', title: 'AI answers from your materials', text: 'Asks a follow-up if needed instead of guessing.' },
        { icon: 'send', title: 'AI sends the right link', text: 'Product page, materials form, or ticket—delivered to the customer phone.' },
        { icon: 'clipboard', title: 'Team picks it up in the dashboard', text: 'Recording, transcript and summary ready the next day, with ticket status to follow up on.' },
      ],
      // Where the number can live: this used to be the "Where customers find the number"
      // card in the blue band. Merged here as one row of touchpoint chips so it does not
      // repeat step 1.
      touchpoints: {
        label: 'Put the number on',
        items: ['Website Contact Us', 'Support page', 'Product manual', 'After-sales card', 'Email signature', 'Packaging'],
      },
      // 服务范围与能力边界：原来分散在产品页上的三处折叠条（其中两处同名）
      // 合并到这里，分组呈现。内容取自原 products.notes + flow.notes + flow.smsDetails。
      // 暂时未使用：产品页页尾的 NoteDisclosure 折叠块「服务范围与能力边界」已下架
      // （见 LandingPage.js 的注释）。这段含对外免责表述，保留以便随时恢复。
      scope: {
        label: 'Service scope and capability boundaries',
        groups: [
          {
            label: 'Not included by default',
            items: [
              'Call forwarding, voicemail, outbound calling and verification-code receiving are not included by default. How standalone numbers are handled, number categories, fees and service scope are confirmed before launch.',
              'Real-time order lookup, inventory and refund operations, and native platform messaging are described separately from knowledge Q&A; capabilities without a completed system connection are not presented as live features.',
            ],
          },
          {
            label: 'Numbers and source attribution',
            items: [
              'The number can sit at several touchpoints—website, product manual, after-sales card, packaging; calls from all of them are handled centrally in one place.',
              'One number across multiple touchpoints does not automatically give precise source attribution. When channel source matters, use separate numbers, customer self-reporting or other verifiable methods, subject to the current supported scope.',
            ],
          },
          {
            label: 'SMS links',
            items: [
              'Before sending, confirm which product, variant or ticket each link belongs to, so the wrong page is not delivered.',
              'SMS status follows the system record and is not guaranteed to reach every handset; if sending fails, tell the customer and continue handling.',
              'Materials a customer submits are filed under the matching call record, and the team reviews and follows up from there.',
            ],
          },
        ],
      },
    },

    // ---------- SMS links ----------
    // The three "layer" cards that used to sit here restated the five-step flow above
    // (entry card ~= step 1, middle card ~= steps 2-4, follow-up card ~= step 5), so the
    // whole block was removed. The entry touchpoints moved under the five-step flow, and
    // this section now carries SMS links only.
    flow: {
      eyebrow: 'SMS links',
      title: 'See how the AI understands a request and responds',
      subtitle:
        'Two of the most common cases: a customer asks about a product and the AI sends the matching page; a customer needs to supply information and the AI sends a form.',
      // 号码与来源说明、短信链接说明已并入 products.scope（产品页底部统一呈现）
      // 模板里这两块是聊天气泡，不是编号步骤：side 'in' = 客户，'out' = AI
      sms: [
        {
          icon: 'shoppingBag',
          scenario: 'Product selection',
          lead: 'The customer asks whether a bag fits; the AI confirms the use case and model first, then texts the product link.',
          chat: [
            { side: 'in', text: 'I just got an Akai MPC Live 3 and need a bag to carry it. Do you have something that fits?' },
            {
              side: 'out',
              text: 'We do. First, how will you use it — subway commuting, driving to rehearsals, or keeping it at home? The three options differ quite a bit.',
            },
            { side: 'in', text: 'Mostly the subway. Sometimes I drive.' },
            {
              side: 'out',
              text: 'Then I’d point you to the VNEX audio workstation backpack, item YPB-0060-001, $69.99. Waterproof shell, and the compatibility list includes the MPC Live 3.',
            },
            { side: 'in', text: 'Can it hold a laptop too?' },
            {
              side: 'out',
              text: 'The description only lists compatible devices and an 8 kg load — no laptop compartment size, so I won’t guess. I can text you the link to check the detail page, and log the laptop question for the support team.',
            },
            { side: 'in', text: 'Okay, send it — 415-555-0132.' },
            {
              side: 'out',
              text: 'Confirmed, 415-555-0132. Sent — the text has the backpack link, and you can also search "Musiin VNEX" on Amazon.',
            },
          ],
          status: 'Product link sent',
          value: 'The buyer opens the link right after the call instead of searching for the product again.',
        },
        {
          icon: 'tool',
          scenario: 'After-sales information',
          lead: 'The customer opens with a refund; the AI matches the model, pins down the cause, then logs the exchange.',
          chat: [
            { side: 'in', text: 'I bought monitor covers two weeks ago. I tried to fit them today and they are too big. I want a refund.' },
            {
              side: 'out',
              text: 'I’m sorry for the trouble. Let me pin this down first — what model are your monitors, and which version did you order?',
            },
            { side: 'in', text: 'The monitors are Yamaha HS5. The order says HS8.' },
            {
              side: 'out',
              text: 'That is the issue: HS8 uses an 8-inch driver, HS5 is 5-inch, and the covers are not interchangeable. The HS5 takes a different cover — item YPB-0014-02, same price, $29.99 for the two-pack.',
            },
            { side: 'in', text: 'Can I exchange it? I already opened and tried it. And who pays the shipping?' },
            {
              side: 'out',
              text: 'Exchange and shipping have to be confirmed by the support team under platform policy — I can’t approve them on their behalf. I’ll write it up clearly: HS8 purchased, actual monitors HS5, item opened, requesting the HS5 version, and asking about shipping. The team will contact you on the next working day.',
            },
            { side: 'in', text: 'Okay. Do I need to take photos?' },
            {
              side: 'out',
              text: 'Two would help most: one of the monitor model plate, and one of the cover on the monitor. With those, the team can assess it much faster.',
            },
          ],
          status: 'Ticket synced to the owner',
          value: 'Model, opened status and the shipping question are captured in one pass, so support does not start over.',
        },
      ],
    },

    // ---------- Product interface content ----------
    interface: {
      eyebrow: 'Inside the product',
      title: 'Manage customer service in one place, every step in view',
      subtitle: 'A QA knowledge base, call records, recordings and transcripts, and correction logs stay in one workspace, so the team can follow up and manage with ease.',
      // 2026-10-19：六个示意小样（假浏览器窗口）已换成后台真实截图，一张一张整幅展示。
      items: [
        {
          icon: 'list',
          title: 'Call records',
          text: 'Every incoming call in one table: answering status, caller number, summary, ticket status and duration. Filter by date, number or status to find the records that need follow-up.',
          img: '/product/call-records.png',
          alt: 'Call records list in the Bizsprite workspace',
        },
        {
          icon: 'fileText',
          title: 'Call details and transcript',
          text: 'Open a record to replay the recording and read the full transcript, so you can see exactly what the customer asked and how the AI answered. No one has to rely on memory.',
          img: '/product/call-detail.png',
          alt: 'Call recording and transcript in the Bizsprite workspace',
        },
        {
          icon: 'check',
          title: 'Correction log',
          text: 'Misheard words are logged with the original text, the corrected result and the linked term, so the AI keeps improving on the calls that follow.',
          img: '/product/correction-log.png',
          alt: 'Correction log in the Bizsprite workspace',
        },
        {
          icon: 'book',
          title: 'Q&A knowledge base',
          text: 'The questions customers ask and the answers the AI should give live in one place. Organize them by category or import a spreadsheet, and every entry can be edited or removed on its own. The AI answers from this content whenever it takes a call.',
          img: '/product/qa-knowledge.png',
          alt: 'Q&A knowledge base list in the Bizsprite workspace',
        },
      ],
      // 暂时未使用：产品页页尾那行小字已下架（见 LandingPage.js 的注释）。保留以便恢复。
      note: 'Automatic error statistics and live transfer are unconfirmed and excluded from current demos.',
    },
    // ---------- 产品页 FAQ（三张截图之后、页脚之前） ----------
    faqTitle: 'Common questions about the product',
    faqSubtitle: 'What the agent can do, what you need before starting, and how call records are kept.',
    faqs: [
      {
        q: 'Which languages does the AI voice agent support?',
        a: 'It handles English today, matching the North American market these numbers serve. If your customers speak other languages, contact us to confirm what can be supported.',
      },
      {
        q: 'Where do the answers come from?',
        a: 'From the knowledge base you upload: product details, common questions, shipping and return policies. The agent answers from those materials instead of inventing specifications or promises.',
      },
      {
        q: 'What happens if the AI cannot answer, or the caller asks for a person?',
        a: 'The call is still recorded in full. Summary, caller number and the exact question stay in the call records, so your team can call back and follow up. Nothing is answered and then forgotten.',
      },
      {
        q: 'How long are recordings and transcripts kept?',
        a: 'Recordings, transcripts and correction logs stay in your account and can be replayed or reviewed at any time, for review and training.',
      },
      {
        q: 'What do I need before I start?',
        a: 'A OneSuite Business account, a North American number, and your product and service materials. Follow the five steps in the launch guide and you can start taking calls.',
      },
      {
        q: 'Which number does the customer see?',
        a: 'A local North American number: a fixed number tied to your brand rather than an unfamiliar international one.',
      },
    ],

    // ---------- Business scenarios ----------
    scenarios: {
      eyebrow: 'Business scenarios',
      title: 'Why the same product fits different kinds of sellers',
      subtitle: 'Every scenario follows the same structure: problem, example call, process, materials, human owners, validation and entry.',
      fieldLabels: {
        problem: 'Typical problem',
        call: 'Example call',
        process: 'Handling process',
        prep: 'Materials to prepare',
        human: 'Human owners',
        validate: 'How to judge a valid trial',
        boundary: 'Boundary',
      },
      items: [
        {
          icon: 'settings',
          demoId: 'case1',
          name: 'Professional parts & accessories brand',
          problem: 'Repeated confirmation of models, compatibility, sizes and variants.',
          call: '“Will this part fit the 2024 Pro model? How do I buy two units?”',
          process: 'Ask for model and conditions → match from materials → send the product link → open a ticket for unknown issues.',
          prep: 'Accurate, maintainable records of models, compatibility and variants.',
          human: 'Critical mismatch decisions and unresolved questions.',
          validate: 'Test with your own hard questions; verify that answers and links are correct.',
          boundary: 'Materials must be accurate and maintainable; fluent answers must not hide critical mismatches.',
        },
        {
          icon: 'shoppingBag',
          demoId: 'case4',
          name: 'DTC brand with real call demand',
          problem: 'After-hours calls and repeated Q&A consume team capacity.',
          call: '“If I order now, when will it ship? Does this style come in other colors?”',
          process: 'Answer outside hours → respond from FAQ → send product links → record follow-up issues.',
          prep: 'FAQ, policies, product materials and an owner.',
          human: 'Calls needing business judgment and follow-up.',
          validate: 'Compare missed after-hours calls and repeated Q&A before vs. after launch.',
          boundary: 'A DTC site does not automatically mean phone demand; real-time order lookup is specified separately.',
        },
        {
          icon: 'tool',
          demoId: 'case5',
          name: 'Device brand after-sales team',
          problem: 'After-sales repeatedly collects model numbers, descriptions and photos.',
          call: '“The device won’t power on. My order is A123—does it need to be shipped back?”',
          process: 'Collect the model and issue → send a link for materials (photos) → hand off to a human ticket.',
          prep: 'Issue categories, required-materials lists and a ticket owner.',
          human: 'Hazard diagnosis and permission-based tasks always stay with humans.',
          validate: 'Check whether materials are collected in one pass and tickets are actually picked up.',
          boundary: 'Hazard diagnosis and privileged operations stay human; a follow-up owner is required.',
        },
      ],
      note: 'Platform differences are explained inside each scenario—allowed contact touchpoints, after-sales responsibilities and native support tasks—instead of a row of platform logos implying every platform support system is already connected.',
    },

    // ---------- Live voice demo ----------
    demo: {
      eyebrow: 'Live demo',
      title: 'Explore AI service across scenarios',
      subtitle:
        'From pre-sales questions to after-sales support, see how AI voice reception handles different communication needs.',
      modalTitle: 'Bizsprite — Interactive Voice Demo',
      modalHint: 'Recorded call · about 1 minute',
      caseLabel: 'Call',
      categoryLabel: 'Category',
      questionLabel: 'Customer question',
      workflowLabel: 'Service workflow',
      playCase: 'Listen to a call demo',
      duration: '~ 1 min',
      cases: [
        {
          id: 'case1',
          accent: 'blue.500',
          title: 'Music equipment',
          category: 'Music equipment / DJ & production gear / MPC Live carrying case',
          question: 'Will this case fit and protect an Akai MPC Live 3? Is there a two-unit offer, and how can I purchase it?',
          workflow: 'Identify the device → check product fit and compatibility → clarify multi-unit pricing policy → send a purchase link by SMS.',
        },
        {
          id: 'case2',
          accent: 'amber.500',
          title: 'Home appliance',
          category: 'Home appliances / Small kitchen appliances / Portable mini blender',
          question: 'My mini blender is not working. Can I resolve it before considering a return?',
          workflow: 'Confirm the symptom → provide safe troubleshooting steps → verify whether the issue is resolved → send the user guide by SMS.',
        },
        {
          id: 'case4',
          accent: 'blue.700',
          title: 'Beauty care',
          category: 'Beauty care / Skincare / Vitamin C serum and moisturizer bundle',
          question: 'Is the serum appropriate for sensitive skin, how should it be used, and is there a bundle?',
          workflow: 'Confirm product use context → give compliant non-medical usage guidance → recommend the relevant bundle → send a product link by SMS.',
        },
        {
          id: 'case5',
          accent: 'amber.600',
          title: 'Outdoor gear',
          category: 'Outdoor gear / Camping equipment / Lantern charging cable',
          question: 'My camping-lantern order arrived without the charging cable. What information is needed to help?',
          workflow: 'Collect order and package details → review the reported missing item → capture the delivery condition → create an after-sales support ticket.',
        },
      ],
    },

    // ---------- Buying guide ----------
    procurement: {
      eyebrow: 'Launch guide',
      title: 'From account setup to daily use, clear operating instructions',
      subtitle: 'Understand each setting along the usage flow and get up to speed with Bizsprite\'s main features.',
      // 五步改成横向轮播（原来竖排占了近两屏），下面这些是轮播自己的文案
      carousel: {
        stepLabel: 'Step',
        pointsLabel: 'What to do',
        hint: 'Swipe to see all five steps',
        prev: 'Previous step',
        next: 'Next step',
        goTo: 'Go to step',
      },
      // Steps follow the official onboarding SOP; each `links` entry points at a real help article id.
      steps: [
        {
          icon: 'user',
          title: 'Create a OneSuite Business account',
          desc: 'Clicking "Get started" takes you to OneSuite Business to apply for the U.S. number and open the communications account. That redirect is the normal path, not a wrong turn.',
          items: [
            'Enter the company name, contact, business email and address',
            'The email code is valid for 15 minutes; check spam first, then resend',
            'Details must match the registered business entity',
          ],
          links: [
            { id: 'register', label: 'Register the account' },
            { id: 'activate-jump', label: 'Why it redirects' },
          ],
        },
        {
          icon: 'card',
          title: 'Pay for the plan and open the accounts',
          desc: 'Once payment succeeds, the OneSuite Business admin account and the Bizsprite AI platform account are created automatically.',
          items: [
            'Choose a plan and billing period, then complete payment',
            'Credentials arrive by email; change the initial password on first login',
            'Check four things in the dashboard: account created, AI module visible, company details correct, plan active',
          ],
          links: [{ id: 'first-login', label: 'First login checklist' }],
        },
        {
          icon: 'dollar',
          title: 'Top up the account',
          desc: 'The account is prepaid. Top up once manually, or turn on auto-recharge so reception never stops on a low balance.',
          items: [
            'Billing → RECHARGE, pick an amount and payment method, then pay',
            'Or enable Auto-Recharge: set the top-up amount and the trigger threshold',
            'Auto-recharge requires accepting the terms, then updating the card on file',
          ],
          links: [
            { id: 'recharge', label: 'How to top up' },
            { id: 'auto-recharge', label: 'Set up auto-recharge' },
          ],
        },
        {
          icon: 'mic',
          title: 'Create the AI agent and upload materials',
          desc: 'This is the step that decides how accurate the answers are. The AI can only answer as well as the material it is given.',
          items: [
            'Create the agent under AI Assistants and fill in its basic settings',
            'Upload the product and FAQ knowledge bases using the provided templates, then link them to the agent',
            'Configure the greeting, service hours and human-handover rules',
          ],
          links: [
            { id: 'create-agent', label: 'Create the agent' },
            { id: 'upload-kb', label: 'Upload materials' },
            { id: 'rules', label: 'Service rules' },
          ],
        },
        {
          icon: 'check',
          title: 'Test before going live',
          desc: 'Only answer real calls after the tests pass. Use questions your customers have actually asked.',
          items: [
            'Configuration check: knowledge base linked, greeting, service hours, handover rules',
            'Call test: reception works, answers are accurate, handover completes',
            'SMS test where the plan supports it, plus spot checks on your most common questions',
          ],
          links: [
            { id: 'checklist', label: 'Pre-launch checklist' },
            { id: 'go-live', label: 'Go live' },
          ],
        },
      ],
      stepNote: 'Actual activation time depends on how complete your materials are, number-application progress, third-party carrier review and configuration complexity.',
      fees: {
        title: 'How the fees are composed',
        text: 'The monthly fee covers AI reception and the number; call minutes, SMS, extra numbers or extensions are billed by actual usage.',
        fields: ['Number fee', 'Subscription', 'Call minutes', 'SMS fees', 'Setup service', 'Overage usage'],
        note: 'Before pricing is frozen, the site shows fee composition and a consultation entry only — no speculative quotes.',
        cta: 'View pricing',
      },
      boundaries: {
        title: 'Service boundaries and data',
        subtitle: 'Three things worth settling before you go live, so nothing is ambiguous afterwards.',
        cards: [
          { icon: 'shield', title: 'Usage boundaries', text: 'The AI answers only within the materials you provide. Complex after-sales, refunds, complaints and custom requests become tickets and go to a human — it does not commit on your behalf.' },
          { icon: 'lock', title: 'Materials and data', text: 'Call recordings, transcripts and customer information are handled per the actual product policies; compliance questions are escalated to a specialist team.' },
          { icon: 'chart', title: 'Verifiable data', text: 'Compare after-hours missed-call rate, handling time for repeat questions and ticket completeness before and after launch — judge by data, not by feel.' },
        ],
      },
    },

    // ---------- FAQ ----------
    faq: {
      title: 'Frequently asked questions',
      subtitle: 'What sellers ask before choosing a number or AI reception.',
      items: [
        {
          q: 'What is the difference between the standalone number and number + AI?',
          a: 'The standalone number provides a branded North American phone entry and your team answers. Number + AI keeps the number and adds AI reception—answering approved questions, asking follow-ups, sending links, and handing unresolved issues into tickets.',
        },
        {
          q: 'We already have people answering calls. Do we need the AI?',
          a: 'Not necessarily. If your coverage is sufficient, the standalone number may be the right fit. AI is worth considering when you have real after-hours calls, repeated product questions, or reception gaps.',
        },
        {
          q: 'What can the AI answer, and what materials do we need to prepare?',
          a: 'It answers within the materials you approve: product tables, compatibility or variant information, FAQ and policies. You provide the materials and a ticket owner; questions needing business judgment stay with your team.',
        },
        {
          q: 'What can SMS links do? Are they guaranteed to arrive?',
          a: 'The AI can send product-selection links and materials-collection links after a call. SMS is not guaranteed to reach every handset, and sending status follows the system record; if sending fails, the AI tells the customer and continues handling.',
        },
        {
          q: 'Can it check orders, process refunds or check inventory in real time?',
          a: 'Those require separate connections to your order, payment or inventory systems. Knowledge Q&A and those real-time connections are described separately; capabilities whose connections are not complete are not presented as live.',
        },
        {
          q: 'How are fees composed, and is a trial available?',
          a: 'Fees may include the number, subscription, call minutes, SMS, setup and overage. Before pricing is frozen, please use the consultation entry; trial and tax terms follow your agreement.',
        },
      ],
    },

    // ---------- Contact ----------
    contact: {
      title: 'Deploy your AI customer service center',
      subtitle: 'Provisioning is handled by our partner, OneSuite Business.',
      cta: 'Get started',
      email: 'support@bizsprite.com',
      phone: '+1 (408) 877-9998',
      location: '168 W Pomona Ave, Monrovia, CA 91016',
    },

    // ---------- About ----------
    about: {
      // Screen 1 - Hero
      hero: {
        eyebrow: 'Brand introduction',
        title: 'AI connects customers and businesses,',
        titleBold: 'so every conversation adds value',
        body:
          'Bizsprite is an AI voice customer service platform for cross-border e-commerce businesses. We give sellers a US local phone number, SMS messaging, automatic AI call answering, product knowledge base Q&A, customer tickets, customer profiles, and data analytics, helping them build a low-cost, high-efficiency and scalable local customer service system in North America.',
        cta: 'Learn About Product',
        caption: 'Product information confirmed on the call is delivered to the customer\'s phone by SMS.',
      },
      // Screen 1.5 - What we do
      focus: {
        eyebrow: 'Our mission',
        title: 'Making intelligent customer service',
        titleBold: 'easier to deploy and easier to manage',
        body:
          'We focus on what businesses actually need across phone reception, information handover and follow-up. With AI voice service and unified service management, we help teams cut repetitive work and concentrate on the customer interactions that matter more.',
        cards: [
          { icon: 'mic', title: 'Answer every call', desc: 'A local number that picks up while the team is asleep, recognising the caller\'s language and intent from the first sentence.' },
          { icon: 'doc', title: 'Answer from your data', desc: 'Replies are grounded in your product catalogue, policies and service rules, rather than in generic model guesses.' },
          { icon: 'clock', title: 'Hand over cleanly', desc: 'Links go out by SMS and unresolved issues become tickets, so follow-up starts with the full context in place.' },
        ],
        caption: 'Reception, follow-up and records in one flow, built for teams selling across time zones.',
      },
      // Screen 2 - Service details
      details: {
        eyebrow: 'Product philosophy',
        title: 'Making every customer interaction',
        titleBold: 'part of one connected service',
        body1:
          'From answering the call and identifying the need to organising information and following up, Bizsprite works to make AI cooperate with your existing workflows, helping businesses build a clearer service mechanism.',
        // 新稿只有一段，body2 置空；LandingPage 里已做条件渲染，空串不会留出空隙
        body2: '',
        cards: [
          { icon: 'mic', title: 'Listen Clearly', sub: 'Confirm what the customer is really asking', bg: 'blue' },
          { icon: 'doc', title: 'Verify Data', sub: 'Ensure answers match enterprise information', bg: 'yellow' },
          { icon: 'shield', title: 'Define Scope', sub: 'Check which actions can be executed', bg: 'yellow' },
          { icon: 'clock', title: 'Keep Records', sub: 'Leave context for follow-up processing', bg: 'blue' },
        ],
        caption: 'Ask the customer\'s questions clearly, and explain the next steps clearly.',
      },
      // Screen 2.5 - Technical capability
      capability: {
        eyebrow: 'Technical capability',
        title: 'Bringing together AI voice, business data and service workflows',
        titleBold: '',
        subtitle:
          'With real-time voice interaction, document grounding and configurable business capabilities, we help businesses apply AI across different customer service scenarios and keep improving the experience as needs evolve.',
        cards: [
          { label: 'Voice', title: 'Real-time speech', desc: 'Low-latency recognition and response, so a call behaves like a conversation instead of a layer of phone menus.' },
          { label: 'Knowledge', title: 'Document grounding', desc: 'Answers are retrieved from your product materials and service rules, with the source kept alongside the reply.' },
          { label: 'Control', title: 'Capability boundaries', desc: 'The actions the agent may take are limited by configuration and authorisation, not left to the model\'s own judgement.' },
          { label: 'Operations', title: 'Records and handover', desc: 'Conversations, links sent and tickets raised are stored together for the team that follows up.' },
        ],
        caption: 'Research methods turned into product constraints.',
      },
      // Screen 4 - Values
      values: {
        eyebrow: 'Our values',
        title: 'Reliable technology and clear service',
        titleBold: 'to build long-term trust',
        subtitle:
          'We focus on real product capability, transparent service information and a sustainable customer experience — so businesses understand where AI applies and keep improving service quality in practice.',
        items: [
          { title: 'Evidence before fluency', desc: 'A fluent answer is worth less than a correct one. When there is no basis, the agent should say so and hand the question over rather than fill the gap with a guess.' },
          { title: 'Boundaries are a feature', desc: 'We would rather the system decline an action than improvise one. Limits are designed in from the start, not patched on afterwards.' },
          { title: 'The team stays in the loop', desc: 'AI reception should reduce the load on a service team, not hide work from it. Everything that happens is visible and followable.' },
          { title: 'Build with the operators', desc: 'Reception rules come from the people who answer the phone today. Every demo starts from a real customer question.' },
        ],
      },
      // Screen 5 - Product principles
      principles: {
        title: 'Every response should be traceable to its source.',
        subtitle:
          'From answer basis to operation scope, to reception records — these are the details we focus on when designing our product.',
        cards: [
          { icon: 'doc', title: 'Grounded Answers', desc: 'Based on enterprise-provided knowledge and product materials, respond to specific questions.', bg: 'white' },
          { icon: 'shield', title: 'Bounded Operations', desc: 'Limit executable actions to current configuration and authorization scope.', bg: 'white' },
          { icon: 'clock', title: 'Tracked Processing', desc: 'Preserve conversation and processing information for follow-up to understand context.', bg: 'white' },
        ],
        bottomTitle: 'Intent in conversations, understood by AI.',
        bottomBody: 'Product materials, operation permissions, and processing records — provided by business systems as basis.',
      },

      // 合作方：解释「立即开通」为什么跳到 OneSuite，以及两家各自负责什么
      partner: {
        eyebrow: 'Partner ecosystem',
        title: 'Technology and telecom capabilities,',
        titleBold: 'supporting AI customer service',
        subtitle:
          'Bizsprite provides the AI voice service capabilities, and through the numbers and telecom services supported by OneSuite Business, gives businesses the foundation for a customer contact entry point.',
        sides: [
          {
            icon: 'users',
            title: 'Bizsprite',
            desc: 'The AI voice agent, the knowledge base built from your product materials, the call summaries, and the tickets your team works from.',
          },
          {
            icon: 'phone',
            title: 'OneSuite Business',
            desc: 'The U.S. and Canada phone numbers, the call minutes, the SMS channel, the billing and the balance — the telecom side of the same service.',
          },
        ],
        jumpTitle: 'What happens after you click "Get started"',
        jumpBody:
          'The button opens the OneSuite Business registration page. The number and the telecom account are created there under the same business identity, and your Bizsprite AI platform account is set up automatically once payment succeeds. Billing questions go to OneSuite; AI questions come to us.',
        jumpNote: 'That redirect is the normal path, not a wrong turn.',
        jumpCta: 'Visit OneSuite Business',
      },
    },

    // ---------- Footer ----------
    footer: {
      tagline: 'A branded North American number and AI reception for cross-border e-commerce sellers.',
      company: 'Company',
      about: 'About us',
      support: 'Resources',
      help: 'Help Center',
      contactUs: 'Contact us',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      rights: '© 2026 Bizsprite. All rights reserved.',
      powered:
        'AI services provide real-time voice interaction and customer-support capabilities.',
      disclaimer:
        'Product features, service content, pricing plans and promotional information shown on this website are for reference only. Actual service scope, feature configuration, fees and cooperation terms are subject to signed agreements, orders or final platform confirmation. Bizsprite reserves the right to adjust, update or modify its products and services.',
    },

    // ---------- Insights ----------
    blog: {
      // Top sub-nav
      subnav: {
        all: 'All posts',
        cases: 'Case studies',
        industry: 'Industry insight',
        product: 'Product practice',
      },
      // Hero
      hero: {
        eyebrow: 'Bizsprite Insights',
        title: 'Notes on',
        titleBold: 'cross-border AI voice.',
        subtitle:
          'Scenario walkthroughs, hands-on notes, and how we think about AI voice reception for cross-border sellers.',
        searchPlaceholder: 'Search cases, playbooks, product notes…',
        tags: [
          { label: 'AI voice', query: 'ai' },
          { label: 'Use cases', query: 'use' },
          { label: 'Missed calls', query: 'missed' },
          { label: 'Pricing', query: 'pricing' },
        ],
      },
      // Search suggestions (shown under the search box)
      search: {
        popularTitle: 'Popular searches',
        suggestTitle: 'Suggested posts',
        emptyTitle: 'No exact match',
        emptyHint: 'Try a broader keyword, or jump into one of these:',
        browseAll: 'Browse all posts',
        hint: 'Try: “missed call”, “after-hours”, “pricing”',
        // Scoped (in-category) search
        keywordsTitle: 'Popular keywords',
        inCategoryTitle: 'In this category',
        scopedPlaceholder: 'Search in this category…',
        scopedEmptyTitle: 'Nothing here yet',
        scopedEmptyHint: 'No post in this category matches that keyword.',
        searchAll: 'Search all posts instead',
      },
      // Featured article (hero card)
      featured: {
        id: 'after-hours-calls',
        tag: 'Industry insight',
        date: 'Oct 10, 2026',
        readTime: '5 min read',
        title: 'Your U.S. customers call after you close. Can your brand pick up?',
        excerpt:
          'Rather than guessing how many orders you lost, start by seeing which calls nobody picked up — and what they were actually asking.',
      },
      // Editor picks (2 cards)
      editorPicks: [
        {
          id: 'product-faq',
          tag: 'How-to guide',
          date: 'Oct 9, 2026',
          readTime: '5 min read',
          title: 'Stop hunting the next winner — can you answer the questions you already have?',
          excerpt: 'High intent is not a guaranteed sale. It means the customer has named the thing standing in the way of their decision.',
        },
        {
          id: 'local-number-trust',
          tag: 'Industry insight',
          date: 'Oct 7, 2026',
          readTime: '6 min read',
          title: 'On a tight support budget, separate marketplace messages from your brand phone line',
          excerpt: 'Marketplace response time and your own phone line are governed by different rules. Picking up a call is not the same as replying to a chat.',
        },
      ],
      // Categories for filter tabs
      categories: [
        { key: 'all', label: 'All' },
        {
          key: 'usecase', label: 'Use cases', tag: 'Use cases',
          keywords: [
            { label: '100 calls', query: '100' },
            { label: 'Phone tag', query: 'phone tag' },
            { label: 'SMS link', query: 'sms' },
            { label: 'Beauty', query: 'beauty' },
          ],
        },
        {
          key: 'industry', label: 'Industry insight', tag: 'Industry insight',
          keywords: [
            { label: '4 rings', query: '4 rings' },
            { label: 'Missed calls', query: 'missed' },
            { label: 'Cost per call', query: 'cost' },
            { label: 'After-hours', query: 'after you close' },
            { label: 'Marketplace', query: 'marketplace' },
          ],
        },
        {
          key: 'howto', label: 'How-to guide', tag: 'How-to guide',
          keywords: [
            { label: 'Time zone', query: 'time-zone' },
            { label: 'Sorting work', query: 'sorting' },
            { label: 'Pre-sales', query: 'next winner' },
          ],
        },
        {
          key: 'product', label: 'Product updates', tag: 'Product updates',
          keywords: [
            { label: 'Pricing', query: 'pricing' },
            { label: 'Tone & pace', query: 'tone' },
            { label: 'SMS templates', query: 'sms' },
            { label: 'Q3 roadmap', query: 'q3' },
          ],
        },
      ],
      // All-posts list
      allPosts: [
        { id: 'after-hours-calls', tag: 'Industry insight', date: 'Oct 10, 2026', readTime: '5 min', img: '/blog/after-hours-calls.png', title: 'Your U.S. customers call after you close. Can your brand pick up?' },
        { id: 'product-faq', tag: 'How-to guide', date: 'Oct 9, 2026', readTime: '5 min', img: '/blog/product-faq.png', title: 'Stop hunting the next winner — can you answer the questions you already have?' },
        { id: 'local-number-trust', tag: 'Industry insight', date: 'Oct 7, 2026', readTime: '6 min', img: '/blog/local-number-trust.png', title: 'On a tight support budget, separate marketplace messages from your brand phone line' },
        { id: 'timezone-gap', tag: 'How-to guide', date: 'Aug 30, 2026', readTime: '5 min', title: 'Cross-border e-commerce time-zone gap, solved by one U.S. number.' },
        { id: '100-calls-test', tag: 'Use cases', date: 'Aug 12, 2026', readTime: '6 min', title: 'We tested 100 AI reception calls — the 3 scenarios that fail most often.' },
        { id: 'four-rings', tag: 'Industry insight', date: 'Jul 28, 2026', readTime: '4 min', title: 'Why U.S. callers hang up after 4 rings — and what that costs sellers.' },
        { id: 'voice-tuning', tag: 'Product updates', date: 'Jul 25, 2026', readTime: '4 min', title: 'New: tune your AI\'s tone, pace and pronunciation per brand.' },
        { id: 'q3-release', tag: 'Product updates', date: 'Jul 10, 2026', readTime: '3 min', title: 'New: outbound reminder calls, pricing update, and what is next in Q3.' },
        { id: 'sms-templates', tag: 'Product updates', date: 'Jul 2, 2026', readTime: '5 min', title: 'New: SMS templates — send the right link before the caller hangs up.' },
        { id: 'beauty-brand', tag: 'Use cases', date: 'Jun 22, 2026', readTime: '5 min', title: 'A beauty brand\'s after-sales calls: from phone tag to one ticket in 30 seconds.' },
        { id: 'sorting-work', tag: 'How-to guide', date: 'Jun 5, 2026', readTime: '4 min', title: 'AI reception is not replacing your team — it is sorting the work for them.' },
        { id: 'cost-missed-call', tag: 'Industry insight', date: 'May 19, 2026', readTime: '5 min', title: 'How much does one missed after-sales call actually cost a U.S. seller?' },
        { id: '3c-brand-sms', tag: 'Use cases', date: 'Apr 28, 2026', readTime: '7 min', title: 'From 12-minute phone tag to a linked SMS: one 3C brand\'s first month.' },
      ],
      // Article detail page copy
      article: {
        back: 'Back to insights',
        toc: 'On this page',
        share: 'Share',
        copyLink: 'Copy link',
        copied: 'Copied',
        writtenBy: 'Written by',
        related: 'Related reading',
        prev: 'Previous',
        next: 'Next',
        ctaTitle: 'Get the next one in your inbox',
        ctaText: 'One email when there is a real update — case study, product change, or industry note.',
        ctaButton: 'Subscribe',
        sideCtaTitle: 'Stay updated',
        sideCtaText: 'New posts in your inbox.',
        notFound: 'This article could not be found.',
        notFoundBack: 'Back to all posts',
      },
      // Section copy
      sections: {
        latest: { title: 'Latest', subtitle: 'What we published most recently — walkthroughs, playbooks and product notes.' },
        viewAll: 'View all',
        news: { title: 'Product updates', subtitle: 'Release notes, feature updates and what is landing in the next quarter.' },
        stories: { title: 'Use cases', subtitle: 'Scenario walkthroughs — how a typical cross-border seller handles night calls and after-sales.' },
      },
      // Watch: video / recording section
      videos: {
        title: 'Videos & recordings',
        subtitle: 'Product demos and real calls — a few minutes each.',
        viewAll: 'View all',
        inCategoryTitle: 'Videos in this category',
        modalNote: 'Demo video coming soon. The player is ready for the real clip.',
        close: 'Close',
        items: [
          {
            id: 'v-answer',
            cat: 'product',
            duration: '2:14',
            tag: 'Product demo',
            title: 'How the AI picks up a U.S. number in under three seconds.',
            desc: 'From ring to greeting, with the caller history already on screen.',
            date: 'Oct 2026',
          },
          {
            id: 'v-afterhours',
            cat: 'usecase',
            duration: '3:40',
            tag: 'Call recording',
            title: 'A real after-hours call — from pickup to a finished ticket.',
            desc: 'The full loop: answer, clarify, create the ticket, send the link.',
            date: 'Oct 2026',
          },
          {
            id: 'v-sms',
            cat: 'product',
            duration: '1:52',
            tag: 'Product demo',
            title: 'Sending the SMS link before the caller hangs up.',
            desc: 'Why the follow-up link beats a promise to call back.',
            date: 'Sep 2026',
          },
          {
            id: 'v-setup',
            cat: 'howto',
            duration: '4:05',
            tag: 'Walkthrough',
            title: 'Set up in 10 minutes: number, greeting and your first FAQ.',
            desc: 'The shortest path from sign-up to a call that ends in a ticket.',
            date: 'Sep 2026',
          },
          {
            id: 'v-cost',
            cat: 'industry',
            duration: '2:48',
            tag: 'Data walkthrough',
            title: 'What one missed after-sales call actually costs — a walkthrough.',
            desc: 'Where the number comes from, and which assumptions move it most.',
            date: 'Aug 2026',
          },
          {
            id: 'v-tone',
            cat: 'product',
            duration: '3:12',
            tag: 'Product demo',
            title: 'Tuning tone, pace and pronunciation for your brand.',
            desc: 'Four dials you can turn without rewriting a single script.',
            date: 'Aug 2026',
          },
        ],
      },
      // Category tiles (the "where to start" section)
      categoryTiles: [
        { icon: 'book', title: 'Getting started', desc: 'Set up your U.S. number, connect your team and answer the first call.', count: 12 },
        { icon: 'headphones', title: 'Voice & reception', desc: 'How the AI answers, transfers, and what callers actually experience.', count: 9 },
        { icon: 'clipboard', title: 'Tickets & follow-up', desc: 'Turning a call into a ticket, an SMS link, and a closed loop.', count: 7 },
        { icon: 'dollar', title: 'Pricing & plans', desc: 'Minutes, numbers, add-ons and how billing actually works.', count: 5 },
      ],
      // Subscribe block
      subscribe: {
        title: 'Get new posts in your inbox',
        subtitle: 'One email when there is a real update — case study, product change, or industry note. No spam.',
        placeholder: 'your@email.com',
        cta: 'Subscribe',
        success: 'Thanks — check your inbox to confirm.',
        error: 'Could not subscribe right now. Please try again later.',
        invalidEmail: 'Please check the email address.',
        tooMany: 'Too many attempts. Please try again later.',
      },
    },

    // ---------- Help Center ----------
    // 结构参考《帮助中心 · 信息架构建议》：搜索优先 → 6 个任务型分类 → 常见问题 + 故障排查 → 联系支持；
    // 文章统一模板：目标 / 前置 / 步骤 / 确认 / 常见问题 / 相关文章。
    // 正文内容来自《AIkerr 客户开通与账户管理操作SOP》；SOP 里标【待确认】的商业政策，
    // 这里如实写「仍在确认中」，不编造已生效的规则。
    help: {
      hero: {
        eyebrow: 'Help Center',
        title: 'Hi, what do you need help with?',
        subtitle: 'Find activation guides, how-to tutorials and common questions.',
        searchPlaceholder: 'Search, e.g. sign-up, number, AI configuration',
        hotLabel: 'Popular:',
        hot: ['sign up', 'number', 'knowledge base', 'refund'],
      },
      quickStart: {
        title: 'First time with Bizsprite?',
        cards: [
          { key: 'guide', title: 'Start here', desc: 'The full path from sign-up to answering your first call.', cta: 'Open the activation guide' },
          { key: 'register', title: 'Registration & account', desc: 'Sign-up, verification and account setup, step by step.', cta: 'Open the registration guide' },
        ],
      },
      hotTitle: 'Popular questions',
      noAnswer: {
        title: 'Still no answer?',
        desc: 'Talk to our support team and we will take it from there.',
        note: 'Email reaches a person, replied within one business day. The phone line is answered by AI — call it to hear the product and ask your own questions.',
        email: 'support@bizsprite.com',
      },
      rules: {
        title: 'Product rules & safety',
        subtitle: 'Scope, data handling and usage limits — clear before you buy or go live.',
        items: [
          { icon: 'shield', title: 'What the AI will and will not handle', desc: 'Which questions the AI covers, and when a human takes over.', text: 'The AI answers only within your uploaded materials. Complex after-sales, refunds, complaints and custom requests become tickets for a human — it will not commit on your behalf.' },
          { icon: 'lock', title: 'Data management & privacy', desc: 'How customer information and call records are handled.', text: 'Recordings, transcripts and customer details are handled under the actual product policy; compliance questions are escalated to the specialist team.' },
          { icon: 'message', title: 'SMS & calling rules', desc: 'How SMS is sent, consent, and usage limits.', text: 'SMS is sent during a call or when following up a ticket, and must comply with local regulations. Call minutes are billed against your plan, with overage charged per use.' },
        ],
      },
      allTopics: 'All topics',
      // 区块大标题单独一个字段：allTopics 还被筛选按钮组复用，不能一起改
      allTopicsTitle: 'Browse by topic',
      allTopicsSub: 'Six topics, one per stage — from activation to daily use.',
      articlesTitle: 'Guides & answers',
      emptyTitle: 'No matching article',
      emptyHint: 'Try another keyword, or pick a topic below.',
      categories: [
        { key: 'start', icon: 'rocket', title: 'Getting started', desc: 'What the service is, and what to have ready before you begin.' },
        { key: 'account', icon: 'user', title: 'Account & activation', desc: 'Sign-up, email verification, first sign-in — and why a third-party account is needed.' },
        { key: 'number', icon: 'phone', title: 'Number & calls', desc: 'Number activation, AI answering, call records and pre-launch testing.' },
        { key: 'ai', icon: 'mic', title: 'Configure your AI', desc: 'Agent, knowledge base and service rules.' },
        { key: 'billing', icon: 'card', title: 'Billing & charges', desc: 'Plans, top-ups, metering, cancellation and refunds.' },
        { key: 'troubleshoot', icon: 'tool', title: 'Troubleshooting', desc: 'Common errors and how to fix them.' },
      ],
      articles: [
        {
          id: 'what-is-aikerr',
          cat: 'start',
          title: 'What you are actually buying',
          read: '3 min',
          summary: 'A North American phone number plus an AI that answers it. SMS, tickets and transcripts all hang off that one call.',
          goal: 'Be able to say, in one sentence, what the service does for your team.',
          before: ['You sell to customers in North America and lose calls to time zones or staffing.'],
          steps: [
            { t: 'A local number becomes your front door', d: 'Customers dial a U.S. number that looks like a local business, not an overseas call centre.' },
            { t: 'The AI answers, working from your own materials', d: 'Product, shipping and after-sales questions are answered from the knowledge base you upload. Anything outside it is escalated rather than improvised.' },
            { t: 'The call ends in something your team can act on', d: 'A link goes out by SMS during the call, and anything unresolved becomes a ticket with the recording, transcript and summary attached.' },
          ],
          confirm: 'You can point at the number, the AI and the ticket queue, and say who owns each one.',
          related: ['two-platforms', 'prepare', 'upload-kb'],
        },
        {
          id: 'two-platforms',
          cat: 'start',
          title: 'Why “Get started” sends you to another site',
          read: '3 min',
          summary: 'Bizsprite supplies the AI. OneSuite Business supplies the U.S. number and the telecom service. Two accounts, one service.',
          goal: 'Understand why activation spans two platforms, and which task belongs where.',
          before: ['You have not registered anywhere yet. Read this first so you do not create two accounts.'],
          steps: [
            { t: 'Bizsprite is the AI', d: 'Creating the agent, uploading your product and FAQ materials, writing the greeting and the hand-off rules.' },
            { t: 'OneSuite Business is the number and the line', d: 'U.S. number provisioning, call minutes, SMS, billing, balance and auto-recharge.' },
            { t: 'The jump is the normal path', d: 'Clicking “Get started” on this site opens the OneSuite Business registration page, so the number and the telecom account are created under the same business identity.' },
          ],
          confirm: 'You know that billing questions go to OneSuite and AI questions come to us.',
          faq: [
            { q: 'Do I have to register with OneSuite Business?', a: 'Yes — the U.S. number and the call minutes are provisioned there. It is the telecom side of the same service, not a separate product.' },
            { q: 'There is a sign-up link on both sites. Which one do I use?', a: 'Start here, then follow the jump. Registering through a second entry point can create a duplicate account, which makes the later binding step messy.' },
          ],
          related: ['activate-jump', 'register', 'first-login'],
        },
        {
          id: 'prepare',
          cat: 'start',
          title: 'Three things to have ready before you start',
          read: '4 min',
          summary: 'Company details, your product materials and a payment method. Half an hour of gathering saves a day of back-and-forth.',
          goal: 'Have everything on hand so activation does not stall halfway through.',
          steps: [
            { t: 'Company details', d: 'Registered company name, a long-lived business email, a contact person and the business address. They should match your actual operating entity — verification is checked against them.', shot: 'Registration form — company name, email, address' },
            { t: 'Product and policy materials', d: 'Product descriptions, FAQ, shipping policy, after-sales policy and refund rules. This is what the AI answers from, so plain files beat polished brochures.' },
            { t: 'A payment method', d: 'Credit card, Alipay or WeChat Pay, depending on what the billing page offers for your account. This is used for the balance top-up.' },
          ],
          confirm: 'You can fill the registration form without leaving your desk to look anything up.',
          related: ['register', 'upload-kb', 'recharge'],
        },
        {
          id: 'activate-jump',
          cat: 'account',
          title: 'What happens after you click “Get started”',
          read: '3 min',
          summary: 'A new tab opens on OneSuite Business. From there: sign up, verify your email, choose a plan, pay — and you end up with two accounts.',
          goal: 'Know the sequence, so the jump does not feel like leaving the product.',
          before: ['Company details, a business email and a payment method are ready.'],
          steps: [
            { t: 'A new tab opens', d: 'The OneSuite Business registration page loads in a new tab, so this site stays open behind it.', shot: 'OneSuite Business registration page' },
            { t: 'Register and verify', d: 'Fill in the company form, then enter the code sent to your email.' },
            { t: 'Choose a plan and pay', d: 'Pick the plan and the billing cycle, then complete payment.' },
            { t: 'Two accounts are created', d: 'The OneSuite Business account for the number and billing, and the AI platform account for the agent. Credentials are emailed to you.' },
          ],
          confirm: 'You have a working sign-in for both platforms in your inbox.',
          related: ['register', 'first-login', 'two-platforms'],
        },
        {
          id: 'register',
          cat: 'account',
          title: 'Registering your OneSuite Business account',
          read: '4 min',
          summary: 'Follow the steps below to create your OneSuite Business account, complete the required information and account verification, and prepare for service configuration.',
          goal: 'Create the business account the U.S. number will be provisioned against.',
          before: ['Company name, contact person, business email and business address.'],
          steps: [
            { t: 'Open the registration form', d: 'Click “Sign Up / Register” on the page you were sent to.', shot: 'OneSuite Business — Sign Up' },
            { t: 'Fill in the company details', d: 'Company name, contact name, business email, business address, plus any additional verification fields the form asks for.', shot: 'Company information form' },
            { t: 'Verify your email', d: 'A code is sent to the address you registered. Enter it to finish.', shot: 'Email verification code screen' },
          ],
          confirm: 'The account dashboard opens and shows your company information.',
          faq: [
            { q: 'How long is the verification code valid?', a: '15 minutes. If it expires, request a new one.' },
            { q: 'Can I use a personal inbox like Gmail?', a: 'It will usually work, but a business domain is better — the account has to stay reachable for as long as the number is live.' },
          ],
          related: ['email-code', 'first-login', 'recharge'],
        },
        {
          id: 'email-code',
          cat: 'account',
          title: 'The verification email never arrived',
          read: '3 min',
          summary: 'Nine times out of ten it is the spam folder or a company mail filter. Work through these four checks in order.',
          goal: 'Get past email verification without opening a second account.',
          steps: [
            { t: 'Check spam and the promotions tab', d: 'The code comes from an automated sender, which is a common false positive for corporate filters.' },
            { t: 'Confirm the address you typed', d: 'A single wrong character sends the code somewhere you cannot read it, and the form will not tell you.' },
            { t: 'Resend once, then wait five minutes', d: 'Requesting repeatedly can rate-limit the sender and delay every code that follows.' },
            { t: 'Still nothing — switch address, or ask us', d: 'Try a different business mailbox, or email support@bizsprite.com with your company name and we will verify on our side.', shot: 'Mailbox showing the verification email' },
          ],
          confirm: 'The dashboard opens, which means the address is verified.',
          related: ['register', 'first-login'],
        },
        {
          id: 'first-login',
          cat: 'account',
          title: 'First sign-in: check these four things',
          read: '3 min',
          summary: 'Sign in, change the initial password, then confirm the account, the AI module, the company details and the plan are all where you expect them.',
          goal: 'Start from a verified, correctly configured account instead of discovering problems mid-call.',
          before: ['The account credentials emailed to you.'],
          steps: [
            { t: 'Sign in and change the password', d: 'Use the initial password from the email, set a new one, then sign in again to confirm it works.' },
            { t: 'Confirm the account was created', d: 'The dashboard should open with your company name on it.', shot: 'OneSuite dashboard' },
            { t: 'Confirm the AI module is visible', d: 'The AI service module should appear in the console — not as an empty or error state.', shot: 'AI service module in the console' },
            { t: 'Confirm company details and plan', d: 'Company information and the purchased plan should both be listed. If either is missing, contact us before going further.' },
          ],
          confirm: 'All four are correct. If not, stop and contact support — configuring an AI on top of a broken account wastes more time than waiting does.',
          related: ['register', 'create-agent', 'recharge'],
        },
        {
          id: 'recharge',
          cat: 'billing',
          title: 'How to top up your balance',
          read: '3 min',
          summary: 'Billing, then Recharge, then amount and payment method. The prepaid balance is what keeps the line answering.',
          goal: 'Add prepaid credit so the service keeps running.',
          before: ['You are signed in to the OneSuite Business console.', 'A payment method is available.'],
          steps: [
            { t: 'Open Billing', d: 'In the console, go to Billing.', shot: 'Console — Billing' },
            { t: 'Click Recharge', d: 'Choose Recharge to start a one-off top-up.', shot: 'Billing — Recharge' },
            { t: 'Choose the amount and the payment method', d: 'Confirm the amount, pick the payment method, and complete the payment.', shot: 'Amount and payment method' },
          ],
          confirm: 'The balance on the Billing page increases, and the top-up appears in the transaction list.',
          related: ['auto-recharge', 'billing-read', 'refund'],
        },
        {
          id: 'auto-recharge',
          cat: 'billing',
          title: 'Set up auto-recharge so calls never stop',
          read: '4 min',
          summary: 'Billing, then Auto-Recharge: tick it on, set the amount and the threshold, then update the card. After that a low balance refills itself.',
          goal: 'Stop a low balance from turning into missed calls overnight.',
          before: ['You are signed in to the OneSuite Business console.'],
          steps: [
            { t: 'Open Billing and find Auto-Recharge', d: 'Billing, then Auto-Recharge.', shot: 'Billing — Auto-Recharge' },
            { t: 'Turn it on', d: 'Tick “Auto-Recharge prepaid credit”.' },
            { t: 'Set the amount and the trigger threshold', d: 'The amount is what gets added each time. The threshold is the balance below which it fires. Set the threshold high enough to cover a busy weekend.', shot: 'Amount and threshold fields' },
            { t: 'Accept the terms and update the card', d: 'Agree to the service terms, then update the payment method and confirm.', shot: 'Payment method confirmation' },
          ],
          confirm: 'Auto-recharge shows as enabled, and the balance tops itself up when it drops below the threshold.',
          faq: [
            { q: 'What threshold should I pick?', a: 'At least a few days of normal traffic. If a missed refill means missed calls, being generous here is cheap insurance.' },
          ],
          related: ['recharge', 'billing-read'],
        },
        {
          id: 'billing-read',
          cat: 'billing',
          title: 'Reading your bill: what is metered, what is flat',
          read: '4 min',
          summary: 'AI answering and AI conversations are unlimited. Call minutes and SMS are metered. The number is a flat monthly fee.',
          goal: 'Know which line items move with volume and which do not.',
          steps: [
            { t: 'Flat items', d: 'The number and the subscription are fixed monthly amounts. They do not change with call volume.' },
            { t: 'Metered items', d: 'Call minutes, SMS and any additional lines or extensions are billed by usage. Every unit price is published on the pricing page.' },
            { t: 'Balance versus bill', d: 'The prepaid balance is what the service draws down in real time. The bill is the record of what was drawn down. A top-up is not a charge — it is the balance being refilled.' },
          ],
          confirm: 'You can tell, from the billing page alone, whether a change in the total came from volume or from a plan change.',
          faq: [
            { q: 'Can I change plans mid-cycle?', a: 'Yes. Upgrades take effect immediately and are prorated for the rest of the cycle; downgrades apply from the next cycle, so you keep everything you already paid for.' },
          ],
          related: ['recharge', 'auto-recharge', 'cancel'],
        },
        {
          id: 'create-agent',
          cat: 'ai',
          title: 'Create the AI agent that answers your calls',
          read: '3 min',
          summary: 'One agent per line. Name it, describe the business, save — you can refine everything after the first test call.',
          goal: 'Have an AI agent that the number can be pointed at.',
          before: ['You can sign in to the AI platform.'],
          steps: [
            { t: 'Open AI Assistants', d: 'In the AI platform, go to “AI Assistants”.', shot: 'Phone settings — greeting and voice', shotImg: '/help/create-agent-0.png' },
            { t: 'Create a new agent', d: 'Give it a name your team will recognise — usually the brand plus the line.' },
            { t: 'Fill in the basic profile', d: 'What the business sells, who calls, and what the agent is allowed to decide on its own.' },
            { t: 'Save', d: 'Confirm and save. The agent now exists, but it knows nothing yet — the next step is the knowledge base.' },
          ],
          confirm: 'The agent is listed under AI Assistants, and can be selected when you configure the number.',
          related: ['upload-kb', 'rules', 'first-login'],
        },
        {
          id: 'upload-kb',
          cat: 'ai',
          title: 'Upload the materials the AI answers from',
          read: '5 min',
          summary: 'Two libraries: products and FAQ. Follow the template, upload, then link both to the agent. What you do not upload, it will not invent.',
          goal: 'Make the AI answer from your real materials rather than from general knowledge.',
          before: ['Your product descriptions, FAQ, shipping, after-sales and refund policies.'],
          steps: [
            { t: 'Upload the product library', d: 'Follow the console template. Model numbers, compatibility, sizes and variants are the fields customers ask about most.', shot: 'Knowledge base — product library template' },
            { t: 'Upload the FAQ library', d: 'Question-and-answer pairs. Write them the way customers actually ask, not the way a policy document phrases it.' },
            { t: 'Link both libraries to the agent', d: 'Uploading is not enough — the agent has to be associated with the libraries, or it will answer with nothing.', shot: 'Linking libraries to the agent' },
            { t: 'Test with three real questions', d: 'Use questions customers actually asked you this month, including one you know is awkward.' },
          ],
          confirm: 'The AI answers your three test questions from your materials, and says it will check with the team for anything outside them.',
          faq: [
            { q: 'What if the AI does not know something?', a: 'It should escalate rather than improvise. If it invents an answer, that is a knowledge-base gap — add the material, or add a rule that sends that intent to a human.' },
          ],
          related: ['create-agent', 'rules', 'checklist'],
        },
        {
          id: 'rules',
          cat: 'ai',
          title: 'Service rules: greeting, hours and hand-off',
          read: '4 min',
          summary: 'The greeting, the schedule the AI covers, and the intents that must always reach a person.',
          goal: 'Decide what the AI handles alone, and what always goes to your team.',
          steps: [
            { t: 'Write the greeting', d: 'Under about 12 seconds: who you are, what you can help with, what happens next. Skip “your call is important to us”.' },
            { t: 'Set the working hours', d: 'Inside your hours, calls can ring the team first and fall through to the AI. Outside them, the AI answers immediately.' },
            { t: 'Define the hand-off rules', d: 'Name the intents that must go to a human: complaints, anything with a legal or safety edge, and anything the AI is not allowed to decide.', shot: 'Rules — hand-off configuration' },
          ],
          confirm: 'You can name, out loud, three things the AI will never decide on its own.',
          faq: [
            { q: 'Are the rule templates finalised?', a: 'The configuration options are in place, but some policy details are still being confirmed. Tell us your scenario and we will confirm what applies to your account before you go live.' },
          ],
          related: ['create-agent', 'upload-kb', 'checklist'],
        },
        {
          id: 'checklist',
          cat: 'number',
          title: 'Pre-launch checklist',
          read: '6 min',
          summary: 'Four blocks: AI configuration, a real call, SMS, and answer quality. Everything has to be ticked before live traffic hits it.',
          goal: 'Catch configuration gaps before your customers do.',
          steps: [
            { t: 'AI configuration', d: 'Agent created · knowledge base uploaded · libraries linked to the agent · greeting set · working hours set · hand-off rules set.' },
            { t: 'A real call', d: 'Call the number from an outside phone. The call connects · the AI answers · a product question is answered correctly · the hand-off to a person works.', shot: 'Test call log' },
            { t: 'SMS', d: 'If your plan includes SMS: send a link during a call and confirm it arrives on the handset.', shot: 'SMS received on a phone' },
            { t: 'Answer quality', d: 'Product questions accurate · FAQ accurate · after-sales handled per policy · unknown questions escalate instead of being invented.' },
          ],
          confirm: 'Every line above is ticked. A failed test call costs nothing; a failed customer call costs a customer.',
          related: ['go-live', 'upload-kb', 'rules'],
        },
        {
          id: 'go-live',
          cat: 'number',
          title: 'Going live',
          read: '3 min',
          summary: 'Once the checklist is clear, the agent can take real calls. Start with the number on one channel, not everywhere at once.',
          goal: 'Put the number in front of real customers without a big-bang switch.',
          before: ['Every item on the pre-launch checklist is ticked.'],
          steps: [
            { t: 'Publish the number on one touchpoint', d: 'Your website contact page is the easiest to add and the easiest to remove. Leave packaging and ad copy for later.' },
            { t: 'Watch the first day', d: 'Read the transcripts. Most week-one fixes are knowledge-base gaps, not model problems.' },
            { t: 'Then widen it', d: 'Add the number to packaging, product pages and support email signatures once the first day looks clean.' },
          ],
          confirm: 'The number is live on at least one channel, and someone is reading the transcripts daily.',
          related: ['checklist', 'billing-read', 'cancel'],
        },
        {
          id: 'cancel',
          cat: 'billing',
          title: 'Cancel your service',
          read: '4 min',
          summary: 'Console, then Company, then Cancel Account. The service stops at the next billing date, not immediately — and stays usable until then.',
          goal: 'Stop the service cleanly, and know what is still charged until it stops.',
          before: ['You are signed in to the OneSuite Business console.'],
          steps: [
            { t: 'Open the Company page', d: 'In the console sidebar, open “Company”.', shot: 'Console — Company' },
            { t: 'Find Cancel Account', d: 'It sits at the bottom-left of that page.', shot: 'Company — Cancel Account' },
            { t: 'Read the cancellation notice', d: 'It sets out when the service stops, how the remaining balance is handled, and how long records are kept.' },
            { t: 'Confirm', d: 'Tick “I understand the impact of cancelling”, then continue to submit the request.', shot: 'Cancellation confirmation' },
          ],
          confirm: 'The cancellation request is recorded, and the stop date is shown.',
          faq: [
            { q: 'Does it stop right away?', a: 'No. The service stops on the next billing date. Until then the account works normally.' },
            { q: 'Can I keep using the account in the meantime?', a: 'Yes — and anything you use in that window is still charged, including outbound calls, AI overage and other out-of-plan usage. Those come out of the prepaid balance.' },
          ],
          related: ['refund', 'billing-read'],
        },
        {
          id: 'refund',
          cat: 'billing',
          title: 'What happens to your remaining balance',
          read: '3 min',
          summary: 'Unused prepaid balance is refunded automatically, typically within 7 business days of the service stopping. The detailed refund rules are still being finalised.',
          goal: 'Know what you get back, and when.',
          steps: [
            { t: 'The balance goes back to the original payment method', d: 'If unused prepaid balance remains when the service stops, the platform processes the refund automatically — usually within 7 business days.', shot: 'Refund record' },
            { t: 'Usage before the stop date still comes out of the balance', d: 'Until the service actually stops, outbound calls, AI overage and other out-of-plan usage are deducted as normal.' },
            { t: 'Detailed refund terms are being confirmed', d: 'The exact conditions, the calculation method, whether the number fee is deducted, and the credit timeline are still being finalised. We will publish them here, and will confirm in writing for your account on request.' },
          ],
          confirm: 'You know the expected timeline, and you know which parts are still subject to confirmation.',
          faq: [
            { q: 'Is the number fee refundable?', a: 'Not confirmed yet. We will not guess — ask us and we will confirm for your account.' },
            { q: 'How long does the refund take to arrive?', a: 'The platform targets 7 business days after the service stops. Your bank or card issuer may add a few days.' },
          ],
          related: ['cancel', 'billing-read'],
        },
      ],
      faqTitle: 'Popular questions',
      faqSubtitle: 'The questions we get most. Open one for the short answer.',
      faq: [
        { q: 'How do I sign up and activate Bizsprite?', a: 'Use “Get started” on this site to register with OneSuite Business, verify your email, pick a plan and top up — then come back to create your AI agent. The full walkthrough is in “Get started”.' },
        { q: 'Do I have to register with OneSuite Business?', a: 'Yes. The U.S. number and the call minutes are provisioned there — it is the telecom side of the same service. See “Why Get started sends you to another site”.' },
        { q: 'How do I configure what the AI says?', a: 'Create an agent in the console, upload your company materials, product and after-sales FAQs, then set the greeting and service hours. The AI answers only from what you upload.' },
        { q: 'What happens when the AI cannot answer?', a: 'It escalates to your team and opens a ticket rather than improvising. Add the missing material to the knowledge base and the same question is covered next time.' },
      ],
      troubleTitle: 'Troubleshooting guide',
      troubleSubtitle: 'See common usage issues and the recommended way to handle each one.',
      trouble: [
        { icon: 'mail', title: 'No verification email', desc: 'Check spam, resend, and if a different mailbox still gets nothing, work through this guide.', to: 'email-code' },
        { icon: 'phone', title: 'Calls are not being answered', desc: 'Check the number status, whether the greeting plays, and whether a forwarding rule is sending the call somewhere else.', to: 'checklist' },
        { icon: 'message', title: 'The SMS link never arrived', desc: 'SMS is not guaranteed to reach the handset. Check the template, the handset, and what the AI told the caller it would send.', to: 'checklist' },
        { icon: 'mic', title: 'The AI gave a wrong answer', desc: 'Almost always a knowledge-base gap. Find the transcript, then add or correct the material it should have used.', to: 'upload-kb' },
        { icon: 'card', title: 'The balance ran out mid-month', desc: 'Turn on auto-recharge so a low balance refills itself before it becomes missed calls.', to: 'auto-recharge' },
        { icon: 'user', title: 'Cannot sign in', desc: 'A wrong password, the wrong mailbox, or the wrong account entity all stop at the same page. Check these four first.', to: 'first-login' },
      ],
      article: {
        home: 'Help Center',
        updatedLabel: 'Updated',
        updatedOn: '2026-10-10',
        tocTitle: 'On this page',
        goalLabel: 'Goal',
        beforeLabel: 'Before you start',
        stepsLabel: 'Steps',
        confirmLabel: 'Done when',
        faqLabel: 'Common questions',
        relatedLabel: 'Related guides',
        helpfulLabel: 'Did this answer your question?',
        helpfulYes: 'Yes',
        helpfulNo: 'Not really',
        helpfulThanks: 'Thanks — noted. Still stuck? Tell us what happened and we will add it here.',
        helpfulReasonLabel: 'What was missing?',
        helpfulReasons: ['Screenshots do not match', 'Steps are incomplete', 'My case is different', 'Not the answer I needed'],
        helpfulMailCta: 'Send us this gap',
        shotLabel: 'Screenshot placeholder',
        backToList: 'Back to Help Center',
        backToCat: 'Back to',
        stillStuck: 'Still stuck?',
        stillStuckCta: 'Contact us',
        notFound: 'That article does not exist.',
      },
      contact: {
        eyebrow: 'Still stuck?',
        title: 'Need more help? Contact our technical team',
        subtitle: 'If the documentation does not answer your question, you can reach us by email or phone.',
        emailLabel: 'Email support',
        emailNote: 'Good for logs, screenshots or account details. Replied within one business day.',
        email: 'support@bizsprite.com',
        demoLabel: 'Phone',
        demoNote: 'Want to hear the product first? Call this number and ask your own questions.',
        demoValue: '+1 (408) 877-9998',
      },
    },
    // ---------- Lead forms (shared) ----------
    // 「联系我们」页的表单字段。原「预约演示」页已随功能下架移除，字段定义保留给联系我们页。
    lead: {
      form: {
        name: 'Full name',
        namePh: 'Jane Chen',
        email: 'Work email',
        emailPh: 'you@company.com',
        emailHint: 'A personal inbox like Gmail is fine — plenty of our sellers use one.',
        phone: 'Phone or WhatsApp',
        phonePh: '+86 138 0000 0000',
        phoneHint: 'Used to reach you about this request only.',
        company: 'Company or store name',
        companyPh: 'Nordwind Outdoor',
        platforms: 'Where do you sell?',
        platformsHint: 'Pick all that apply.',
        platformOptions: ['Amazon', 'Shopify', 'TikTok Shop', 'eBay', 'Walmart', 'Temu', 'Own site', 'Not selling yet'],
        volume: 'Customer service call volume',
        volumeOptions: ['Under 100', '100 – 500', '500 – 2,000', '2,000+', 'Not sure yet'],
        message: 'Anything we should know?',
        messagePh: 'e.g. We miss most calls between 9pm and 6am, and returns take three days to answer.',
        optional: 'Optional',
        consentPre: 'I agree to be contacted by email or phone about this request, and I have read the ',
        consentLink: 'Privacy Policy',
        consentPost: '.',
        submit: 'Send request',
        sending: 'Sending…',
        requiredNote: 'Fields marked * are required.',
        errName: 'Please tell us your name.',
        errEmail: 'Please enter a valid email address.',
        errPhone: 'Please enter a number we can reach you on.',
        errCompany: 'Please tell us your company or store name.',
        errPick: 'Please pick one.',
        errConsent: 'Please tick the consent box before sending.',
        errSubmit: 'We could not send your request. Please try again, or email support@bizsprite.com.',
        errTooMany: 'Too many requests from this address. Please try again later, or email support@bizsprite.com.',
      },
      aside: {
        email: 'support@bizsprite.com',
        phone: '+1 (408) 877-9998',
        trustTitle: 'Before you send',
        trust: [
          'We reply within one business day, in English or Chinese.',
          'Your details are used to answer this request only — never resold or added to a mailing list.',
          'No number porting and no contract until you decide to go ahead.',
        ],
      },
      success: {
        title: 'Request received',
        contactText: 'We will reply within one business day at the email you gave us. If it is urgent, you can also call us directly and get an answer right away.',
        again: 'Send another request',
      },
    },

    // ---------- Contact us ----------
    // 注意：key 叫 contactPage，不要叫 contact —— 顶层已经有一个 contact 块
    // （首页底部那条 CTA 带用的），重名会被后者覆盖，导致首页那条带子变空。
    contactPage: {
      eyebrow: 'Contact us',
      title: 'Find the AI customer service plan that fits your business',
      subtitle: 'Tell us about your business scenarios, service needs and goals. Our team will walk you through product capabilities, how they apply to your case and related service details, and help you evaluate the right option.',
      topicLabel: 'What is this about?',
      topicOptions: ['Product questions', 'Pricing and volume', 'Technical support', 'Partnership or reseller', 'Something else'],
      formNote: 'We reply to the email you give us within one business day, in English or Chinese.',
      channelsTitle: 'Get in touch with our team',
      channels: [
        { icon: 'mail', label: 'Email', value: 'support@bizsprite.com', href: 'mailto:support@bizsprite.com', note: 'Answered within one business day.' },
        { icon: 'phone', label: 'Phone', value: '+1 (408) 877-9998', href: 'tel:+14088779998', note: 'A U.S. number, if you would rather talk.' },
        { icon: 'pin', label: 'Office', value: '168 W Pomona Ave, Monrovia, CA 91016', href: '', note: 'Come by if you are local.' },
      ],
      routingTitle: 'Which channel for what',
      routing: [
        { label: 'How something works, or something broke', via: 'Email us', note: 'Send your store name and a screenshot.' },
        { label: 'Pricing, call volume, annual terms', via: 'The form on this page', note: 'Pick “Pricing and volume” so it reaches sales.' },
        { label: 'Partnership, reseller, agency', via: 'Email us', note: 'Put “Partnership” in the subject line.' },
        { label: 'Abuse, or a number that called you wrongly', via: 'Email us', note: 'Put “Abuse” in the subject line.' },
      ],
    },

  },

  // =====================================================================
  // 中文
  // =====================================================================
  zh: {
    langLabel: 'English',
    nav: {
      home: '首页',
      products: '产品',
      pricing: '定价',
      cases: '通话演示',
      resources: '资源中心',
      about: '关于我们',
      blog: '资讯中心',
      contact: '联系我们',
      openAccount: '立即开通',
    },

    hero: {
      badge: '专为跨境企业打造',
      titleLine1: '让 AI 连接每一次客户沟通，',
      titleBold1: '',
      titleLine2: '助力企业高效服务与增长',
      titleBold2: '',
      subtitle:
        '通过专属北美号码与 AI 语音客服，自动响应客户来电，协助完成咨询解答、需求识别与后续业务跟进，让客户服务更加及时、连贯。',
      ctaPrimary: '了解解决方案｜体验 AI 服务',
      stats: [
        { value: '24/7', label: '全天候自动接听' },
        { value: '美国', label: '北美本地号码' },
        { value: '通话 → 短信 → 工单', label: '服务链路不断档' },
      ],
      // 首页 hero 右侧：四张卡轮播，每张 5 秒（原来是「听 AI 怎么处理一通来电」播放卡）。
      // 开通流程 / 部署 由上线指南五步拆成两段；价格直接复用下方 pricing 块，不重复维护文案。
      carousel: {
        regionLabel: '首页重点轮播',
        goTo: '切换卡片',
      },
      slides: [
        {
          key: 'launch',
          tag: '开通流程',
          title: '从付款到号码可用，四步完成',
          subtitle: '开通由合作方 OneSuite Business 承接，路径是固定的。',
          steps: [
            { title: '注册 OneSuite Business 账户', text: '填写公司名、联系人、企业邮箱与地址，邮箱验证码 15 分钟内有效。' },
            { title: '选择套餐并完成付款', text: '付款成功后，OneSuite 管理账户与 Bizsprite AI 平台账户自动创建。' },
            { title: '为账户充值', text: '账户为预付费。手动充值一次，或开启自动充值，避免余额不足时停接。' },
            { title: '号码开通完成', text: '资料齐备后约 3–7 个工作日，美国本地号码即可用于接听。' },
          ],
        },
        {
          key: 'deploy',
          tag: '部署配置',
          title: '资料越完整，回答越准确',
          subtitle: '这一步决定回答质量——AI 只在你给到的资料范围内回答。',
          steps: [
            { title: '创建 AI 智能体', text: '在 AI Assistants 里新建，并填写基础设置。' },
            { title: '上传商品与 FAQ 资料', text: '用提供的模板整理商品主表、SKU、兼容型号、物流与售后政策。' },
            { title: '配置服务规则', text: '设定欢迎语、服务时段与转人工规则。' },
            { title: '上线前测试', text: '用客户真实问过的问题，测接听、回答与转人工，通过后再接真实来电。' },
          ],
        },
        {
          key: 'price',
          tag: '价格',
          title: '一个月费，AI 接听不限量',
          subtitle: '月订阅覆盖 AI 接听与美国本地号码；通话分钟与短信按实际用量计。',
        },
        {
          key: 'guarantee',
          tag: '服务保障',
          title: '先跑一个月，不满意全额退',
          subtitle: '决定之前，先把顾虑说清楚。',
          items: [
            { icon: 'shield', title: '首月不满意全额退', text: '支付首月费用；30 天内不满意，可申请全额退款，不收手续费。' },
            { icon: 'clock', title: '约 3–7 个工作日开通', text: '资料齐备后即可提交号码申请，实际时效以运营商审核为准。' },
            { icon: 'headset', title: '邮箱与电话支持', text: '支持邮箱一个工作日内回复；紧急问题可直接打电话，当场确认。' },
          ],
          cta: '查看定价',
        },
      ],
    },

    // ---------- Home teaser (product + cases preview) ----------
    homeTeaser: {
      productEyebrow: '我们的产品',
      productTitle: '一个号码、一套 AI，按业务阶段选择',
      productSubtitle: '从「只有号码」到「号码 + AI」，两种配置对应两种业务阶段。',
      productBadge: '主推',
      aiFeatures: [
        '24/7 自动接听，覆盖夜班和周末',
        '用你的商品资料回答常见问题',
        '通话中发链接，复杂问题自动成单',
        '通话录音、摘要、工单一并交接',
      ],
      standaloneCta: '了解产品',
      standaloneCtaAlt: '收听通话演示',
      flowTitle: '一通来电的完整流程',
      flowSteps: ['接听', '回答', '链接', '工单'],
      flowNote: '每一通电话都按这个顺序处理，不漏接，也不遗漏交接。',
      casesEyebrow: '真实通话',
      casesTitle: '看看 AI 怎么接你的真实来电',
      casesSubtitle: '三个最常见的跨境电商场景，点一下就能听对应的通话实录。',
      caseLink: '收听通话演示',
      casesMoreCta: '查看全部通话演示',
    },

    // ---------- 首页价值三卡 ----------
    valueCards: {
      eyebrow: '核心价值',
      title: '连接客户需求，让服务更高效、更连贯',
      subtitle: 'AI 语音客服融入日常业务流程，从客户来电到信息处理，帮助企业提升响应效率与服务质量。',
      items: [
        {
          title: '全天候响应客户来电',
          text: 'AI 自动接听非工作时间的客户来电，记录咨询需求，并将关键信息同步至团队，确保后续服务及时衔接。',
        },
        {
          title: '智能处理常见咨询',
          text: '基于企业产品资料与知识库，AI 自动解答型号、兼容性、尺寸等常见问题，减少重复沟通，让团队专注于更重要的客户需求。',
        },
        {
          title: '统一管理售后信息',
          text: '在通话过程中向客户发送相关资料链接，并根据提交的信息生成服务工单，减少重复收集与沟通成本。',
        },
        {
          title: '持续优化 AI 服务质量',
          text: '支持按问题类型查看 AI 回答记录，自动记录修改内容与时间，方便团队及时纠正错误，持续提升回答准确性。',
        },
      ],
    },

    // ---------- Home flow (touchpoints + map + SMS) ----------
    homeFlow: {
      eyebrow: '触点聚合',
      title: '一个品牌号码，让客户沟通无缝衔接',
      subtitle: '整合官网、产品包装及品牌服务页面的联系电话，通过统一的北美号码承接客户咨询，提供一致、连贯的服务体验。',
      sources: [
        { name: '品牌官网', text: '电话咨询入口' },
        { name: '产品包装', text: '售后联系方式' },
        { name: '品牌服务页', text: '品牌联系电话' },
      ],
      mapBadge: '北美顾客来电',
      mapCalloutTitle: '你的北美号码',
      mapCalloutSub: 'AI 依据资料接听',
      nextStep1Title: '先把问题回答清楚',
      nextStep1Text: 'AI 依据商品资料回答；资料未覆盖时会先追问确认，再决定是否发送链接。',
      nextStep2Title: '再把下一步交到顾客手里',
      nextStep2Text: '商品链接、补资料入口与正式工单按顺序发出，团队上线后可直接接手。',
      flow1Label: '商品推荐',
      flow1Title: '电话里确认好的商品，发到顾客手机里',
      flow1Sub: '征得顾客同意后，发送对应商品链接。',
      flow1Steps: ['确认商品', '征得同意', '商品短信'],
      flow2Label: '未解决的问题',
      flow2Title: '工单链接发过去，资料补齐，团队继续跟进',
      flow2Sub: '说明后续并征得同意，再发送补资料入口。',
      flow2Steps: ['预工单', '短信入口', '补资料', '正式工单', '团队跟进'],
    },

    pricing: {
      eyebrow: '定价',
      titleLine1: '灵活透明的定价，',
      // 同英文侧：只对 AI 接听不限量，通话分钟与短信按实际用量计费
      titleLine2: '满足不同阶段的业务需求',
      subtitle:
        '根据企业的通话需求与服务规模，选择适合的 AI 语音客服方案，以清晰的费用结构管理客户服务成本。',
      planLabel: '按月订阅 · Starter',
      price: '$66',
      perMonth: '/月',
      wasPrice: '$99',
      priceNote: '* 首发优惠价，活动结束后恢复 $99/月',
      features: [
        'AI 自动接听来电 + AI 客户对话',
        '美国本地号码 1 个 · 30+ 自然音色',
        '商品知识库问答 · 工单自动创建',
      ],
      refundTitle: '首月不满意，全额退款',
      refundText:
        '先支付首月费用；30 天内如认为不适用，可申请全额退款，不收取手续费。',
      contactSales: '联系我们',
      getStarted: '立即开通',
      assurances: [
        '按月订阅 · 无隐藏费用',
        '资料齐全约 3–7 个工作日开通',
        '美国本地号码 · 可选区号',
      ],
      // 四张卡是上面费用明细表的概括：表里 8 个分组归成这 4 类
      otherCostsTitle: '费用构成',
      otherCostsText: '了解各项服务的计费方式与适用条件，合理规划日常运营支出。',
      otherCosts: [
        { icon: 'FiPhone', title: '号码与开通', text: '一次性开通费 + 每月保号费。主号、外线、分机分别计费，部分项目用满 3 个月免收。' },
        { icon: 'FiMic', title: '语音通话', text: '呼入 $0.015 / 分钟，呼出 $0.025 / 分钟，按实际通话分钟计。' },
        { icon: 'FiMessageSquare', title: '短信', text: '一次性开通费 + 每月保号费，收发按条计。AI 把商品链接发到客户手机，走的就是这条通道。' },
        { icon: 'FiZap', title: 'AI 服务', text: '实时语音 AI 按分钟、对话与数据按用量、录音存档与知识库按量，平台与 AI 订阅按月。' },
      ],
      faqsTitle: '关于产品定价的常见问题',
      faqsSubtitle: '了解订阅费用、服务计费、使用范围及方案调整等相关信息。',
      faqs: [
        { q: '$66 是永久价吗？', a: '$66 是首发优惠月费，活动结束后恢复 $99/月。' },
        { q: '会产生预期之外的超额费用吗？', a: 'AI 接听和 AI 对话本身不限量；通话分钟、短信是电信服务商按量计费，单价都公开列在页面上。' },
        { q: '年付是否有优惠？', a: '企业、渠道或年付客户可能有不同政策，具体折扣请联系销售顾问确认。' },
        { q: '是否可以随时取消？', a: '取消和退款需要根据套餐、付款周期和已产生的通信资源费用确认，我们会帮你走流程。' },
      ],

      // ---- 费用明细（第二屏）----
      feeBreakdown: {
        eyebrow: '费用明细',
        title: '按需使用，费用清晰可控',
        subtitle: '按照业务模式、使用场景及服务需求，灵活配置适合的产品方案。',
        col: { item: '计费项目', unit: '计费单位', price: '单价 (USD)', note: '备注' },
        notBilled: '暂不单独计费',
        tbd: '待定',
        sections: [
          {
            label: '电信服务',
            groups: [
              {
                label: '号码与开通',
                items: [
                  { item: '北美企业程控电话开通费', unit: '一次性', price: '$15.00', note: '含1主线+2分机，默认1路AI客服' },
                  { item: '外线增设费', unit: '一次性、每条', price: '$15.00', note: '增设外线；使用满 3 个月免（待确认）' },
                  { item: '外线保号费', unit: '每月每线', price: '$10.00', note: '减免条件待确认' },
                  { item: '内部分机增设费', unit: '一次性、每条', price: '$10.00', note: '—' },
                  { item: '内部分机保号费', unit: '每月每分机', price: '—', note: '—' },
                  { item: '增加外线开通费', unit: '每月', price: '$10.00', note: '使用满3个月免' },
                  { item: '增加分机license费', unit: '每月', price: '$10.00', note: '—' },
                ],
              },
              {
                label: '语音',
                items: [
                  { item: '语音呼入', unit: '每分钟', price: '$0.015', note: '支持来电号码显示' },
                  { item: '语音呼出', unit: '每分钟', price: '$0.025', note: '支持呼出号码显示' },
                ],
              },
              {
                label: '短信',
                items: [
                  { item: '短信(SMS)开通费', unit: '一次性', price: '$15.00', note: '企业号码 SMS 服务（接收转邮箱 + 推送模式化短信）；使用满 3 个月免' },
                  { item: '短信(SMS)保号', unit: '每月', price: '$5.00', note: '减免条件待确认' },
                  { item: '短信接收费', unit: '每条', price: '$0.015', note: '接收客户短信并转发至企业邮箱' },
                  { item: '短信推送费', unit: '每条', price: '$0.03', note: '通过公共短信平台发送模式化短信' },
                ],
              },
            ],
          },
          {
            label: 'AI 服务',
            groups: [
              {
                label: '智能语音',
                items: [
                  { item: '实时语音AI', unit: '每分钟', price: '$0.085', note: 'AI实时语音对话处理' },
                  { item: '大模型推理', unit: '—', price: '$0', note: '{{notBilled}}' },
                  { item: '电话通信', unit: '每分钟', price: '$0', note: '{{notBilled}}' },
                ],
              },
              {
                label: '对话与数据',
                items: [
                  { item: '对话文字记录', unit: '每千字符', price: '$0.003', note: '通话文字转录' },
                  { item: '对话智能', unit: '每千字符', price: '$0', note: '{{notBilled}}' },
                  { item: '客户档案', unit: '每档案每日', price: '$0', note: '{{notBilled}}' },
                  { item: '客户记忆', unit: '每次', price: '$0.0035', note: '—' },
                  { item: '知识库', unit: '每次', price: '$0', note: '{{notBilled}}' },
                  { item: '知识库', unit: '每GB每月', price: '$0', note: '{{notBilled}}' },
                ],
              },
              {
                label: '录音与存档',
                items: [
                  { item: '录音及转文字', unit: '—', price: '$0.0001', note: '通话录音并转文字存档' },
                ],
              },
              {
                label: '平台与订阅',
                items: [
                  { item: '短信(AI部分)', unit: '每条', price: '{{tbd}}', note: '费用归属待确认' },
                  { item: '平台固定费', unit: '—', price: '{{tbd}}', note: '内部成本项，单价待定' },
                  { item: 'AI订阅费', unit: '月', price: '{{tbd}}', note: '内部成本项，单价待定' },
                ],
              },
            ],
          },
        ],
      },
    },

    // ---------- Case demos hero ----------
    casesHero: {
      eyebrow: '通话演示',
      title: '聆听真实对话，',
      titleBold: '了解 AI 语音客服的实际业务表现',
      subtitle: '通过不同业务场景的通话示例，了解 AI 如何识别客户需求、进行自然交流，并协助完成信息收集与服务响应。',
      ctaPrimary: '查看产品演示',
      stats: [
        { value: '7×24', label: '全天候 AI 接听' },
        { value: '4 类', label: '典型跨境场景' },
        { value: '真实', label: '通话录音可回放' },
      ],
    },

    products: {
      eyebrow: '两种选择',
      title: '面向不同业务需求的',
      titleBold: 'AI 客户服务解决方案',
      subtitle: '从智能电话接待到业务流程协同，帮助企业构建更加高效、灵活的客户服务体系。',
      // 「立即开通」会跳出本站 —— 只保留按钮下方这一行。
      // 措辞去掉了原来「这次跳转是正常流程，不是走错站」的防御性说法，
      // 越强调「不是走错站」越像在解释，改成中性陈述即可。
      partnerInline:
        '「立即开通」会打开合作方 OneSuite Business 的注册页，美国号码与通信账户在那里创建。',
      // 暂时未使用：原产品页卡片区下方那条整条说明卡已下架（原因见 LandingPage.js 里的注释）。
      // 完整解释在「关于我们 → 合作方」那一屏。要恢复产品页的卡片，把这段接回 JSX 即可。
      partnerNote: {
        title: '为什么「立即开通」会离开本站',
        text: 'Bizsprite 提供 AI。美国号码和它背后的通信服务由合作方 OneSuite Business 提供 —— 号码、通话分钟、短信通道和账单都在那边。同一个服务，两个账户，中间的跳转是正常流程。',
        cta: '前往 OneSuite Business',
      },
      // 痛点：模板里是独立的一行四张卡，不再包在深色引言面板里，所以这里只留 items
      painPoints: {
        items: [
          {
            icon: 'moon',
            title: '夜班电话接不到',
            text: '美国客户深夜来电，团队已经下班，电话进入语音信箱——或者被竞争对手接走。',
          },
          {
            icon: 'repeat',
            title: '重复问题持续占用人力',
            text: '兼容性、尺寸、退换货，团队每天重复回答几乎一样的十个问题。',
          },
          {
            icon: 'clipboard',
            title: '售后信息分散，难以归集',
            text: '照片、订单号与问题描述分散在电话、留言和邮件中，难以拼成完整工单。',
          },
          {
            icon: 'globe',
            title: '缺少美国号码，品牌可信度不足',
            text: '客户看到个人手机号或陌生区号，会降低对品牌的信任。',
          },
        ],
      },
      choice: {
        solvesLabel: '解决什么问题',
        featuresLabel: '包含内容',
        aiBadge: '推荐',
      },
      cards: [
        {
          kind: 'number',
          name: '单独号码',
          tagline: '建立专属客户联络入口，统一品牌沟通体验',
          solves: ['没有美国号码的品牌问题', '提升品牌可信度'],
          features: ['一个美国本地企业号码', '转接到你指定的设备或线路', '标准语音服务'],
          note: '适合团队自行接听、只需要一个本地号码的卖家。',
          aside: { label: '按需', text: '以号码套餐为准' },
          cta: '了解产品',
        },
        {
          kind: 'ai',
          name: '号码 + AI Voice Agent',
          tagline: '智能响应客户来电，提升服务效率',
          solves: ['夜班来电不漏接', '重复问题一次答清', '售后信息自动整理为工单'],
          features: [
            '美国本地号码（区号依库存可选）',
            '7×24 AI 接听与作答',
            '短信发链接、售后工单',
            '录音、转写、摘要留存',
          ],
          note: '适合来电量多、需要自动接待和完整记录的中小跨境卖家。',
          price: { amount: '$66', unit: '/月', fine: '（原价 $99 · 首月不满意全额退款）' },
          cta: '立即开通',
          ctaAlt: '收听通话演示',
        },
      ],
      flowTitle: '从客户来电到后续处理，实现流程衔接',
      flowSubtitle: '从接听到跟进，各环节信息在同一流程中留存，减少重复沟通与信息遗漏，让服务衔接更高效。',
      flowStepLabel: '步骤',
      flow: [
        { icon: 'phone', title: '客户拨打号码', text: '从你放置号码的任一触点拨入。' },
        { icon: 'headphones', title: 'AI 接起并听懂', text: '识别需求——下单、兼容性、售后。' },
        { icon: 'message', title: 'AI 依据你的资料作答', text: '资料未覆盖时会先追问确认，不做无依据的推测。' },
        { icon: 'send', title: 'AI 发出对应链接', text: '商品页、补资料表单或工单，直接送到客户手机。' },
        { icon: 'clipboard', title: '团队在后台接手', text: '录音、转写与摘要次日可查，并可直接跟进工单状态。' },
      ],
      // 号码可以放在哪些地方：原来在蓝色带「客户在哪里找到号码」那一卡里。
      // 合并到五步流程下方做一行触点标签，避免和步骤 1 重复叙述。
      touchpoints: {
        label: '号码可放在',
        items: ['官网 Contact Us', 'Support 页面', '产品说明书', '售后卡', '邮件签名', '包装'],
      },
      // 服务范围与能力边界：原来分散在产品页上的三处折叠条（其中两处同名）
      // 合并到这里，分组呈现。内容取自原 products.notes + flow.notes + flow.smsDetails。
      // 暂时未使用：产品页页尾的 NoteDisclosure 折叠块「服务范围与能力边界」已下架
      // （见 LandingPage.js 的注释）。这段含对外免责表述，保留以便随时恢复。
      scope: {
        label: '服务范围与能力边界',
        groups: [
          {
            label: '默认不包含的能力',
            items: [
              '转接、语音信箱、外呼和验证码接收不默认包含；单号实际承接方式、号码分类、费用及服务范围在开通前确认。',
              '实时订单查询、库存与退款操作，以及原生平台消息连接，与知识库问答分开说明；未完成系统对接的能力不作为已上线功能。',
            ],
          },
          {
            label: '号码与来源',
            items: [
              '号码可以放在官网、说明书、售后卡、包装等多个触点；这些触点产生的来电都进入同一个后台集中处理。',
              '同一个号码出现在多个触点时，不自动提供精确的来源归因。如需渠道来源，应采用不同号码、客户主动说明或其他可验证方式，并以当前支持范围为准。',
            ],
          },
          {
            label: '短信链接',
            items: [
              '每条链接对应哪个商品、变体或工单，都在发送前确认，避免发错页面。',
              '短信以系统记录的发送状态为准，不保证 100% 到达；发送失败时告知客户并继续处理。',
              '客户提交的资料会归档到对应的通话记录下，团队在后台统一查看并跟进。',
            ],
          },
        ],
      },
    },

    // 蓝色带：原来这里先是「三层链路」三张卡，内容与上面的五步流程逐条重复
    // （入口卡≈步骤 1、中间卡≈步骤 2–4、跟进卡≈步骤 5），已整块删除。
    // 入口触点并到五步流程下方，这一节现在只承载短信链接。
    flow: {
      eyebrow: '短信链接',
      title: '了解 AI 如何理解需求并提供服务',
      subtitle: '两种最常见的场景：客户问商品，AI 发对应页面；客户要补资料，AI 发一张表单。',
      // 号码与来源说明、短信链接说明已并入 products.scope（产品页底部统一呈现）
      // 模板里这两块是聊天气泡，不是编号步骤：side 'in' = 客户，'out' = AI
      sms: [
        {
          icon: 'shoppingBag',
          scenario: '商品选型',
          lead: '客户问这个包能不能装，AI 先问清使用场景和型号，再把商品链接发到手机上。',
          chat: [
            { side: 'in', text: '我刚买了台 Akai MPC Live 3，想买个包带着走，你们有合适的吗？' },
            {
              side: 'out',
              text: '有的。先确认下使用场景：主要坐地铁通勤、开车去排练，还是放在家里？三类选择不太一样。',
            },
            { side: 'in', text: '主要坐地铁，偶尔开车。' },
            {
              side: 'out',
              text: '那推荐 VNEX 音频工作站背包，编号 YPB-0060-001，69.99 美元，防水面料，兼容列表里有 MPC Live 3。',
            },
            { side: 'in', text: '它能装下笔记本电脑吗？' },
            {
              side: 'out',
              text: '商品描述里只写了兼容机型和 8 公斤承重，没标电脑仓尺寸，这个我不猜。我把链接发到您手机上，您看下详情页；同时帮您记一条问题，让客服确认。',
            },
            { side: 'in', text: '行，发吧，415-555-0132。' },
            {
              side: 'out',
              text: '确认是 415-555-0132。已发出，短信里是这款背包的链接，也可以直接在亚马逊搜 “Musiin VNEX”。',
            },
          ],
          status: '商品链接已发送',
          value: '客户挂掉电话就能打开链接，不用再回去搜商品名。',
        },
        {
          icon: 'tool',
          scenario: '售后补资料',
          lead: '客户开口就要退货，AI 先把型号对上、把问题定位清楚，再登记换货。',
          chat: [
            { side: 'in', text: '我两周前买的音箱罩，今天装的时候套不上，大了一圈，我要退货。' },
            {
              side: 'out',
              text: '很抱歉给您添麻烦了。先帮您把情况确认清楚：您的音箱是什么型号？下单时选的又是哪款？',
            },
            { side: 'in', text: '音箱是雅马哈 HS5，订单上写的是 HS8。' },
            {
              side: 'out',
              text: '问题找到了：HS8 是 8 寸单元，HS5 是 5 寸，两款罩子不通用。HS5 对应另一款，编号 YPB-0014-02，价格一样，29.99 美元两个装。',
            },
            { side: 'in', text: '那能换吗？已经拆开装过了。运费谁出？' },
            {
              side: 'out',
              text: '换货和运费要客服按平台政策确认，我不能替他们答应。我会把情况写清楚：买的是 HS8，实际是 HS5，已拆封，希望换 HS5 款，并询问运费。客服下一个工作日联系您。',
            },
            { side: 'in', text: '好，照片要拍吗？' },
            {
              side: 'out',
              text: '拍两张最好：一张音箱的型号铭牌，一张罩子套上去的样子。有这两张，客服判断会快很多。',
            },
          ],
          status: '工单已同步至售后负责人',
          value: '型号、拆封状态、运费诉求一次记清，客服不用从头再问。',
        },
      ],
    },

    interface: {
      eyebrow: '产品界面',
      title: '统一管理客户服务，让每一步清晰可见',
      subtitle: '问答知识库、通话记录、录音转写与纠错记录集中留存，服务进展与业务数据在同一处查看，便于团队跟进与管理。',
      // 2026-10-19：六个示意小样（假浏览器窗口）已换成后台真实截图，一张一张整幅展示。
      items: [
        {
          icon: 'list',
          title: '客服记录',
          text: '每一通来电都在同一张表里：接听状态、主叫号码、通话摘要、工单状态与通话时长。可按日期、号码或状态筛选，快速找到需要跟进的记录。',
          img: '/product/call-records.png',
          alt: '客服记录列表界面',
        },
        {
          icon: 'fileText',
          title: '通话详情与转写',
          text: '打开一条记录即可回放录音、查看完整的对话转写，确认客户问了什么、AI 答了什么，后续跟进不必再靠回忆。',
          img: '/product/call-detail.png',
          alt: '通话录音与对话转写界面',
        },
        {
          icon: 'check',
          title: '纠错记录',
          text: '识别有偏差的内容集中记录在这里，标注原文、纠错结果与关联术语，便于持续修正 AI 的回答，让后续通话更准确。',
          img: '/product/correction-log.png',
          alt: '纠错记录界面',
        },
        {
          icon: 'book',
          title: '问答知识库',
          text: '客户常问的问题与标准答案集中在这里，可按分类整理，也支持从表格批量导入。每一条都能单独编辑或删除，AI 接听时按这份内容作答。',
          img: '/product/qa-knowledge.png',
          alt: '问答知识库列表界面',
        },
      ],
      // 暂时未使用：产品页页尾那行小字已下架（见 LandingPage.js 的注释）。保留以便恢复。
      note: '自动错误统计、实时转接等尚未确认的能力，暂不在当前演示中。',
    },
    // ---------- 产品页 FAQ（三张截图之后、页脚之前） ----------
    faqTitle: '关于产品的常见问题',
    faqSubtitle: '了解 AI 语音客服的能力范围、开始前的准备，以及通话记录的留存方式。',
    faqs: [
      {
        q: 'AI 语音客服支持哪些语言？',
        a: '目前以英语接待北美客户，与号码覆盖的市场一致。如果客户使用其他语言，可以先联系我们确认适配方式。',
      },
      {
        q: 'AI 依据什么内容回答客户？',
        a: '依据你上传到知识库的资料，包括产品信息、常见问题、物流与退换货政策。AI 按这些内容作答，不会自行编造产品参数或服务承诺。',
      },
      {
        q: 'AI 答不上来，或者客户要求转人工怎么办？',
        a: '这通电话仍会完整记录，通话摘要、客户号码和具体问题都会留在客服记录里，团队可以据此回电跟进，不会出现接了却没人跟进的情况。',
      },
      {
        q: '通话录音和转写会保存多久？',
        a: '录音、对话转写与纠错记录都会保留在账户内，可随时回听和查看，便于复盘与培训。',
      },
      {
        q: '开始使用前需要准备什么？',
        a: '一个 OneSuite Business 企业账户、一个北美本地号码，以及你的产品资料与服务说明。按帮助中心的上线指南走完五步即可开始接听。',
      },
      {
        q: '客户看到的来电号码是哪个国家的？',
        a: '北美本地号码。客户看到的是一个固定的品牌专属号码，而不是陌生的国际号码。',
      },
    ],

    scenarios: {
      eyebrow: '业务场景',
      title: '不同行业，都能用同一套接待逻辑',
      subtitle: '三个典型场景，分别说明 AI 能解决什么问题、人工负责什么、以及能力的边界在哪。',
      fieldLabels: {
        problem: '典型业务问题',
        call: '客户来电例子',
        process: '产品处理过程',
        prep: '需要准备的资料',
        human: '人工负责什么',
        validate: '如何判断试用有效',
        boundary: '需要说明的边界',
      },
      items: [
        {
          icon: 'settings',
          demoId: 'case1',
          name: '专业配件品牌',
          problem: '型号、兼容性、尺寸与变体需要反复确认。',
          call: '“这个配件能装在 2024 款 Pro 型号上吗？买两件怎么下单？”',
          process: '追问型号与条件 → 依据资料匹配 → 发送商品链接 → 未知问题开工单。',
          prep: '准确、可维护的型号、兼容性与变体资料。',
          human: '关键错配判断与未知问题承接。',
          validate: '用自己的难问题测试，核对答案与链接是否准确。',
          boundary: '资料必须准确且可维护；流畅回答不能掩盖关键错配。',
        },
        {
          icon: 'shoppingBag',
          demoId: 'case4',
          name: '已有电话需求的独立站品牌',
          problem: '夜间来电、重复答疑占用人力。',
          call: '“现在下单什么时候发货？这款还有别的颜色吗？”',
          process: '非工作时间接听 → 依据 FAQ 回答 → 发送商品链接 → 记录后续问题。',
          prep: 'FAQ、政策、商品资料与负责人。',
          human: '需要业务判断的来电与后续跟进。',
          validate: '对比上线前后夜间漏接与重复答疑的处理情况。',
          boundary: '独立站不自动等于电话需求；实时查单能力须单独说明。',
        },
        {
          icon: 'tool',
          demoId: 'case5',
          name: '设备品牌售后团队',
          problem: '售后反复收集型号、问题描述与图片。',
          call: '“设备开不了机，订单号 A123，需要寄回吗？”',
          process: '收集型号与问题 → 发送补资料（含图片）链接 → 人工工单交接。',
          prep: '问题分类、所需资料清单与工单负责人。',
          human: '危险诊断与业务权限任务，一律保留人工。',
          validate: '检查资料能否一次收齐、工单是否有人接续。',
          boundary: '危险诊断与权限操作保留人工；必须有后续负责人。',
        },
      ],
      note: '平台差异放在具体场景内说明：允许的联系方式触点、售后责任与原生客服任务。避免用一排平台标识，造成“已经连接全部平台客服系统”的误解。',
    },

    demo: {
      eyebrow: '体验演示',
      title: '探索不同场景下的智能客户服务',
      subtitle:
        '从售前咨询到售后支持，体验 AI 语音客服在不同沟通需求下的应用方式。',
      modalTitle: 'Bizsprite — 交互式语音演示',
      modalHint: '通话实录 · 约 1 分钟',
      caseLabel: '通话',
      categoryLabel: '品类',
      questionLabel: '客户问题',
      workflowLabel: '服务流程',
      playCase: '收听通话演示',
      duration: '约 1 分钟',
      cases: [
        {
          id: 'case1',
          accent: 'blue.500',
          title: '音乐设备',
          category: '音乐设备 / DJ 与音乐制作设备 / MPC Live 收纳保护包',
          question: '产品能否适配并保护 Akai MPC Live 3？购买两件是否有优惠，如何购买？',
          workflow: '确认设备型号 → 查询商品与兼容性 → 说明多件购买价格规则 → 短信推送购买链接。',
        },
        {
          id: 'case2',
          accent: 'amber.500',
          title: '家用电器',
          category: '家用电器 / 小型厨房电器 / 便携榨汁机',
          question: '便携榨汁机无法使用，在考虑退货前能否先解决问题？',
          workflow: '确认故障现象 → 提供安全排查步骤 → 确认问题是否解决 → 短信推送使用指南。',
        },
        {
          id: 'case4',
          accent: 'blue.700',
          title: '美容护理',
          category: '美容护理 / 护肤品 / 维 C 精华与保湿套装',
          question: '敏感肌是否适合使用精华？如何使用，是否有套装？',
          workflow: '确认使用场景 → 提供合规的非医疗使用建议 → 推荐对应套装 → 短信推送商品链接。',
        },
        {
          id: 'case5',
          accent: 'amber.600',
          title: '户外装备',
          category: '户外装备 / 露营设备 / 露营灯充电线',
          question: '露营灯订单里少了充电线，需要提供哪些信息才能协助处理？',
          workflow: '收集订单与包装信息 → 核对客户反馈的缺失配件 → 记录到货情况 → 创建售后支持工单。',
        },
      ],
    },

    procurement: {
      eyebrow: '上线指南',
      title: '从账户开通到日常使用，获取清晰的操作指引',
      subtitle: '按照使用流程了解各项设置，快速掌握 Bizsprite 的主要功能。',
      // 五步改成横向轮播（原来竖排占了近两屏），下面这些是轮播自己的文案
      carousel: {
        stepLabel: '步骤',
        pointsLabel: '操作要点',
        hint: '左右滑动，查看全部五步',
        prev: '上一步',
        next: '下一步',
        goTo: '跳到第',
      },
      // 五步流程来自官方《客户开通与账户管理操作 SOP》，
      // 每步的 links 指向帮助中心里真正对应的文章 id。
      steps: [
        {
          icon: 'user',
          title: '注册 OneSuite Business 账户',
          desc: '点官网「立即开通」会跳转到 OneSuite Business，完成美国号码申请与通信账户创建。这是正常的开通流程，并非跳转错误。',
          items: [
            '填写企业名称、联系人、企业邮箱与企业地址',
            '邮箱验证码 15 分钟内有效；没收到先查垃圾邮件，再点重新发送',
            '资料需与实际经营主体保持一致',
          ],
          links: [
            { id: 'register', label: '注册企业账户' },
            { id: 'activate-jump', label: '为什么会跳转' },
          ],
        },
        {
          icon: 'card',
          title: '完成套餐支付，创建账户',
          desc: '支付成功后，系统自动创建 OneSuite Business 管理账户与 Bizsprite AI 平台账户。',
          items: [
            '选择套餐与支付周期，填写支付信息完成付款',
            '账号密码会发到注册邮箱；首次登录后请先修改初始密码',
            '进入后台核对四项：账户已创建、AI 模块正常、企业信息正确、套餐已生效',
          ],
          links: [{ id: 'first-login', label: '首次登录核对四项' }],
        },
        {
          icon: 'dollar',
          title: '为账户充值',
          desc: '账户为预付制。可以手动充值一次，也可以开启自动充值，避免余额不足中断接听。',
          items: [
            '在 Billing 账单页点 RECHARGE 充值，选择金额与支付方式完成付款',
            '或开启 Auto-Recharge：设置每次充值金额与触发阈值',
            '自动充值需要先同意服务条款，再更新自动付款的信用卡信息',
          ],
          links: [
            { id: 'recharge', label: '如何充值' },
            { id: 'auto-recharge', label: '设置自动充值' },
          ],
        },
        {
          icon: 'mic',
          title: '创建 AI Agent，上传资料',
          desc: '这一步决定回答的准确度：AI 的回答质量取决于资料的完整程度。',
          items: [
            '在 AI Assistants 页面创建 Agent，按业务需求配置基础信息',
            '按系统目录与模板上传产品知识库与 FAQ 知识库，并关联到 Agent',
            '配置欢迎语、服务时间与转人工规则',
          ],
          links: [
            { id: 'create-agent', label: '创建 Agent' },
            { id: 'upload-kb', label: '上传资料' },
            { id: 'rules', label: '服务规则' },
          ],
        },
        {
          icon: 'check',
          title: '上线前测试与验收',
          desc: '测试通过后再正式对外接听。建议直接使用顾客真实问过的问题进行测试。',
          items: [
            '配置检查：知识库已关联、欢迎语、服务时间、转人工规则',
            '通话测试：接听是否正常、回答是否准确、转人工是否走通',
            '短信测试（套餐支持时），以及高频问题的回答抽检',
          ],
          links: [
            { id: 'checklist', label: '上线前检查清单' },
            { id: 'go-live', label: '正式上线' },
          ],
        },
      ],
      stepNote: '实际开通时间受资料完整度、号码申请进度、第三方通信服务商审核与系统配置复杂度影响。',
      // 费用构成：从原来平铺的 6 张卡里单独拎出来，它跟「步骤」本来就不是一类东西
      fees: {
        title: '费用由哪些部分构成',
        text: '月费包含 AI 接听与号码；通话分钟、短信、额外号码 / 分机按实际用量计费。',
        fields: ['号码费用', '订阅费用', '通话分钟', '短信费用', '配置服务', '超额用量'],
        note: '价格冻结前，官网仅展示费用构成与咨询入口，具体报价以销售确认为准。',
        cta: '查看定价',
      },
      // 使用边界 / 资料与数据 / 可核验数据 三张合到一块，它们说的是同一件事：上线后的边界与依据
      boundaries: {
        title: '服务边界与数据',
        subtitle: '上线之前把这三件事说明白，后续配合会更顺畅。',
        cards: [
          { icon: 'shield', title: '使用边界', text: 'AI 只回答资料范围内的问题。复杂售后、退款、投诉、定制需求会生成工单并转人工，不会自行承诺。' },
          { icon: 'lock', title: '资料与数据', text: '通话录音、转写与客户信息都按实际产品政策管理；涉及合规问题会转专业团队确认。' },
          { icon: 'chart', title: '可核验数据', text: '对比上线前后的夜间漏接率、重复咨询处理时间与工单完整度，以数据判断效果，而非凭主观感受。' },
        ],
      },
      // 原「体验入口」CTA（预约演示 / 拨打演示号码）随预约功能下架一并移除，本段只保留三张边界卡。
    },

    faq: {
      title: '商家常问的问题',
      subtitle: '选择号码或 AI 接待前，最常被问到的问题与直接的回答。',
      items: [
        {
          q: '单独号码和号码 + AI 有什么区别？',
          a: '单独号码就是给你一个美国号码，团队自己接；号码加 AI 是在号码基础上让 AI 先接电话，回答资料范围内的商品问题，复杂的自动生成工单交给人工。',
        },
        {
          q: '我们已经有人接电话了，还需要 AI 吗？',
          a: '不一定。如果现有排班已经能够覆盖，单独号码即可满足需求；如果存在夜间漏接、重复咨询较多或售后反复索要资料的情况，增加 AI 接听更有价值。',
        },
        {
          q: 'AI 能回答什么？要准备哪些资料？',
          a: 'AI 在你上传的资料范围内回答：商品主表、SKU / ASIN、商品链接、兼容型号、尺寸、FAQ、物流和售后政策。你们需要指定一个工单负责人，业务判断类问题还是人工处理。',
        },
        {
          q: '短信链接有什么作用？一定能收到吗？',
          a: 'AI 可以在通话后发送商品购买链接或补资料链接。短信不保证 100% 到达，发送状态以系统记录为准；如果发送失败，AI 会告知客户并继续处理。',
        },
        {
          q: '能实时查订单、退款或查库存吗？',
          a: '这些需要单独接入 Shopify、Amazon 或你们的 ERP。当前知识问答和实时查单是两个能力，后者如果没有完成系统连接，我们不会当作已上线功能宣传。',
        },
        {
          q: '费用怎么组成？能试用吗？',
          a: '费用包含号码、月订阅、实际通话分钟、短信、额外号码 / 分机、以及可选的配置服务。具体试用政策和含税条件请联系销售确认。',
        },
      ],
    },

    contact: {
      title: '快速部署您的 AI 客服中心',
      subtitle: '开通由合作伙伴 OneSuite Business 提供。',
      cta: '立即开通',
      email: 'support@bizsprite.com',
      phone: '+1 (408) 877-9998',
      location: '168 W Pomona Ave, Monrovia, CA 91016',
    },

    // ---------- 关于我们 ----------
    about: {
      // 第一屏 - Hero
      hero: {
        eyebrow: '品牌介绍',
        title: '以 AI 连接客户与企业，',
        titleBold: '让每一次沟通创造更多价值',
        body:
          'Bizsprite 是一款面向跨境电商企业的 AI 智能语音客服平台，通过为企业提供美国本地电话号码、短信接收、AI 自动接听、产品知识库问答、客户工单、客户档案和数据分析能力，帮助跨境卖家建立低成本、高效率、可规模化的北美本地客服体系。',
        cta: '了解产品',
        caption: '电话中确认的商品信息，通过短信送达顾客手机。',
      },
      // 第一屏半 - 我们做什么
      focus: {
        eyebrow: '我们的使命',
        title: '让智能客户服务',
        titleBold: '更易部署、更易管理',
        body:
          '我们关注企业在电话接待、信息传递和业务跟进中的实际需求，通过 AI 语音客服与统一的服务管理能力，帮助团队减少重复工作，专注于更有价值的客户互动。',
        cards: [
          { icon: 'mic', title: '接通每一通来电', desc: '本地号码在团队休息时依然接起，并从第一句话起识别顾客的语言与来意。' },
          { icon: 'doc', title: '用企业资料回答', desc: '答复基于商品目录、政策与服务规则，而不是模型的泛泛猜测。' },
          { icon: 'clock', title: '把后续交代清楚', desc: '链接通过短信送达，未解决的问题生成工单，跟进时上下文是完整的。' },
        ],
        caption: '接待、跟进与记录在同一条流程里完成，为跨时区销售的团队而设计。',
      },
      // 第二屏 - 服务细节
      details: {
        eyebrow: '产品理念',
        title: '让每一次客户互动，',
        titleBold: '都成为连贯服务的一部分',
        body1:
          '从来电接待、需求识别到信息整理与后续跟进，Bizsprite 致力于让 AI 与现有业务流程协同工作，帮助企业建立更清晰的服务机制。',
        // 新稿只有一段，body2 置空；LandingPage 里已做条件渲染，空串不会留出空隙
        body2: '',
        cards: [
          { icon: 'mic', title: '听清需求', sub: '确认顾客真正要问什么', bg: 'blue' },
          { icon: 'doc', title: '核对资料', sub: '让答复对应企业的信息', bg: 'yellow' },
          { icon: 'shield', title: '明确范围', sub: '核对可以执行的动作', bg: 'yellow' },
          { icon: 'clock', title: '留下记录', sub: '把上下文留给后续处理', bg: 'blue' },
        ],
        caption: '把顾客的问题问清楚，也把下一步交代清楚。',
      },
      // 第二屏半 - 技术能力
      capability: {
        eyebrow: '技术能力',
        // 新稿是一整句，titleBold 留空 → head() 里 boldTitle 为 falsy，只渲染单行标题
        title: '融合 AI 语音、业务信息与服务流程',
        titleBold: '',
        subtitle:
          '结合实时语音交互、文档支持与可配置的业务能力，帮助企业在不同客户服务场景中应用 AI，并根据实际需求持续优化服务体验。',
        cards: [
          { label: '语音', title: '实时语音', desc: '低延迟的识别与应答，让通话像一次对话，而不是一层层按键菜单。' },
          { label: '知识', title: '文档核对', desc: '答复从商品资料与服务规则中检索得出，并把依据与回答一并保留。' },
          { label: '控制', title: '能力边界', desc: 'AI 可以执行的动作由配置与授权决定，而不交给模型自行判断。' },
          { label: '运营', title: '记录与交接', desc: '通话、已发链接与已建工单存放在一起，供后续跟进的团队查看。' },
        ],
        caption: '把研究方法，落实成产品里的约束。',
      },
      // 第四屏 - 价值观
      values: {
        eyebrow: '我们的价值观',
        title: '以可靠的技术与清晰的服务，',
        titleBold: '建立长期信任',
        subtitle:
          '我们注重真实的产品能力、透明的服务信息与可持续的客户体验，让企业能够清楚了解 AI 的适用范围，并在实际使用中持续改善服务质量。',
        items: [
          { title: '有依据，再开口', desc: '一句流利的回答，不如一句正确的回答。没有依据时，AI 应当说明情况并转交，而不是补一句猜测。' },
          { title: '边界是功能，不是限制', desc: '我们宁愿系统拒绝一个动作，也不愿它临场发挥。边界是设计出来的，不是事后补上的。' },
          { title: '团队始终在环里', desc: 'AI 接待应当减轻服务团队的压力，而不是把工作藏起来。发生的每一件事都可查看、可跟进。' },
          { title: '和一线一起做产品', desc: '接待规则来自今天真正在接电话的人。每一次演示，都从顾客的真实问题开始。' },
        ],
      },
      // 第五屏 - 产品原则
      principles: {
        title: '每一步回应，都应有据可查。',
        subtitle:
          '从回答依据到操作范围，再到接待记录，都是我们设计产品时关注的细节。',
        cards: [
          { icon: 'doc', title: '回答有依据', desc: '依据企业提供的知识与商品资料，回应具体问题。', bg: 'white' },
          { icon: 'shield', title: '操作有范围', desc: '将可执行的动作限定在当前配置与授权范围内。', bg: 'white' },
          { icon: 'clock', title: '处理有记录', desc: '保留通话与处理信息，让后续跟进了解前因后果。', bg: 'white' },
        ],
        bottomTitle: '对话中的意图，由 AI 理解。',
        bottomBody: '商品资料、操作权限与处理记录，由业务系统提供依据。',
      },

      // 合作方：解释「立即开通」为什么跳到 OneSuite，以及两家各自负责什么
      partner: {
        eyebrow: '合作生态',
        title: '协同技术与通信服务能力，',
        titleBold: '支持企业开展 AI 客户服务',
        subtitle:
          'Bizsprite 提供 AI 语音客服相关能力，并通过 OneSuite Business 支持的号码及通信服务，为企业构建客户沟通入口提供基础。',
        sides: [
          {
            icon: 'users',
            title: 'Bizsprite',
            desc: 'AI 语音客服、依据你商品资料搭建的知识库、通话摘要，以及团队日常处理的工单。',
          },
          {
            icon: 'phone',
            title: 'OneSuite Business',
            desc: '美国与加拿大本地号码、通话分钟、短信通道，以及账单与余额 —— 同一个服务的通信侧。',
          },
        ],
        jumpTitle: '点「立即开通」之后会发生什么',
        jumpBody:
          '按钮会打开 OneSuite Business 的注册页。号码与通信账户在那里以同一个企业身份创建，付款成功后 Bizsprite AI 平台账户自动开通。账单问题找 OneSuite，AI 问题找我们。',
        jumpNote: '这次跳转是正常流程，不是走错站。',
        jumpCta: '前往 OneSuite Business',
      },
    },

    footer: {
      tagline: '给跨境电商卖家一个北美本地号码，让 AI 先行接听，团队持续跟进服务。',
      company: '公司',
      about: '关于我们',
      support: '资源',
      help: '帮助中心',
      contactUs: '联系我们',
      privacy: '隐私政策',
      terms: '服务条款',
      rights: '© 2026 Bizsprite. 保留所有权利。',
      powered: 'AI 服务用于提供实时语音交互与客户支持功能。',
      disclaimer:
        '本网站所展示的产品功能、服务内容、价格方案及相关宣传信息仅供参考，具体服务范围、功能配置、收费标准及合作条款以双方实际签署的协议、订单或平台最终确认为准。Bizsprite 保留对产品与服务内容进行调整、更新或变更的权利。',
    },

    // ---------- 资讯中心 ----------
    blog: {
      subnav: {
        all: '全部文章',
        cases: '场景实战',
        industry: '行业洞察',
        product: '产品实践',
      },
      hero: {
        eyebrow: 'Bizsprite Insights',
        title: '关于跨境 AI 语音的',
        titleBold: '真实记录。',
        subtitle: '场景拆解、一线观察，以及我们关于跨境 AI 语音接待的思考。',
        searchPlaceholder: '搜索案例、实操指南、产品动态…',
        tags: [
          { label: 'AI 语音', query: 'ai' },
          { label: '场景实战', query: '场景' },
          { label: '漏接电话', query: '漏接' },
          { label: '价格', query: '价格' },
        ],
      },
      // 搜索联想（搜索框下方）
      search: {
        popularTitle: '大家在搜',
        suggestTitle: '猜你想看',
        emptyTitle: '没找到完全匹配的',
        emptyHint: '换个更宽泛的关键词，或者看看这些：',
        browseAll: '浏览全部文章',
        hint: '试试：「漏接」「夜班」「价格」',
        // 分类内搜索
        keywordsTitle: '热门词条',
        inCategoryTitle: '本分类文章',
        scopedPlaceholder: '在此分类中搜索…',
        scopedEmptyTitle: '这个分类里没有',
        scopedEmptyHint: '本分类内没有文章匹配这个关键词。',
        searchAll: '改为搜索全部文章',
      },
      featured: {
        id: 'after-hours-calls',
        tag: '行业洞察',
        date: '2026 年 10 月 10 日',
        readTime: '5 分钟阅读',
        title: '美国客户在你下班后来电，品牌接得住吗？',
        excerpt:
          '与其猜测漏掉了多少订单，不如先看清哪些来电没有被接住，以及客户真正在问什么。',
      },
      editorPicks: [
        {
          id: 'product-faq',
          tag: '实操指南',
          date: '2026 年 10 月 9 日',
          readTime: '5 分钟阅读',
          title: '别只找下一个爆款：已经到来的咨询，你回答清楚了吗？',
          excerpt: '高意向咨询不等于必然成交。它意味着客户已经提出了明确的决策障碍。',
        },
        {
          id: 'local-number-trust',
          tag: '行业洞察',
          date: '2026 年 10 月 7 日',
          readTime: '6 分钟阅读',
          title: '客服预算有限时，先分清平台消息和品牌电话',
          excerpt: '平台站内响应和品牌自有电话由不同规则管理。接起一通电话，并不等于回复了平台聊天。',
        },
      ],
      categories: [
        { key: 'all', label: '全部' },
        {
          key: 'usecase', label: '场景实战', tag: '场景实战',
          keywords: [
            { label: '100 通实测', query: '100' },
            { label: '美妆', query: '美妆' },
            { label: '短信链接', query: '短信' },
            { label: '3C', query: '3c' },
          ],
        },
        {
          key: 'industry', label: '行业洞察', tag: '行业洞察',
          keywords: [
            { label: '4 声铃响', query: '4 声' },
            { label: '漏接电话', query: '漏接' },
            { label: '漏接成本', query: '亏' },
            { label: '下班后来电', query: '下班' },
            { label: '平台消息', query: '平台消息' },
          ],
        },
        {
          key: 'howto', label: '实操指南', tag: '实操指南',
          keywords: [
            { label: '客服时差', query: '时差' },
            { label: '工单分流', query: '工单' },
            { label: '替代人工', query: '替代' },
            { label: '售前咨询', query: '咨询' },
          ],
        },
        {
          key: 'product', label: '产品动态', tag: '产品动态',
          keywords: [
            { label: '价格', query: '价格' },
            { label: '语气语速', query: '语气' },
            { label: '短信模板', query: '短信' },
            { label: 'Q3 路线图', query: 'q3' },
          ],
        },
      ],
      allPosts: [
        { id: 'after-hours-calls', tag: '行业洞察', date: '2026 年 10 月 10 日', readTime: '5 分钟', img: '/blog/after-hours-calls.png', title: '美国客户在你下班后来电，品牌接得住吗？' },
        { id: 'product-faq', tag: '实操指南', date: '2026 年 10 月 9 日', readTime: '5 分钟', img: '/blog/product-faq.png', title: '别只找下一个爆款：已经到来的咨询，你回答清楚了吗？' },
        { id: 'local-number-trust', tag: '行业洞察', date: '2026 年 10 月 7 日', readTime: '6 分钟', img: '/blog/local-number-trust.png', title: '客服预算有限时，先分清平台消息和品牌电话' },
        { id: 'timezone-gap', tag: '实操指南', date: '2026 年 8 月 30 日', readTime: '5 分钟', title: '跨境电商的客服时差，怎么用一个美国号码解决？' },
        { id: '100-calls-test', tag: '场景实战', date: '2026 年 8 月 12 日', readTime: '6 分钟', title: '我们测了 100 通 AI 客服电话，发现最容易出错的 3 个场景' },
        { id: 'four-rings', tag: '行业洞察', date: '2026 年 7 月 28 日', readTime: '4 分钟', title: '为什么美国顾客听到 4 声铃响就挂？这件事卖家每天都在亏' },
        { id: 'voice-tuning', tag: '产品动态', date: '2026 年 7 月 25 日', readTime: '4 分钟', title: '新功能：按品牌调整 AI 的语气、语速与读音' },
        { id: 'q3-release', tag: '产品动态', date: '2026 年 7 月 10 日', readTime: '3 分钟', title: '新功能：外呼提醒通话、套餐价格调整，以及 Q3 路线图' },
        { id: 'sms-templates', tag: '产品动态', date: '2026 年 7 月 2 日', readTime: '5 分钟', title: '新功能：短信模板 —— 在客户挂断前，把正确的链接发出去' },
        { id: 'beauty-brand', tag: '场景实战', date: '2026 年 6 月 22 日', readTime: '5 分钟', title: '一个美妆品牌的售后电话：从反复拉扯，到 30 秒生成一张工单' },
        { id: 'sorting-work', tag: '实操指南', date: '2026 年 6 月 5 日', readTime: '4 分钟', title: 'AI 客服不是替代人工，而是把工单分得更好' },
        { id: 'cost-missed-call', tag: '行业洞察', date: '2026 年 5 月 19 日', readTime: '5 分钟', title: '一通漏接的售后电话，到底让美国卖家亏掉多少钱？' },
        { id: '3c-brand-sms', tag: '场景实战', date: '2026 年 4 月 28 日', readTime: '7 分钟', title: '从 12 分钟的反复拉扯，到一条短信链接：一个 3C 品牌的第一个月' },
      ],
      // 文章详情页文案
      article: {
        back: '返回资讯中心',
        toc: '本文目录',
        share: '分享',
        copyLink: '复制链接',
        copied: '已复制',
        writtenBy: '作者',
        related: '相关阅读',
        prev: '上一篇',
        next: '下一篇',
        ctaTitle: '新文章第一时间送到你邮箱',
        ctaText: '有真实更新时一封邮件：场景实战、产品变动或行业观察。',
        ctaButton: '订阅',
        sideCtaTitle: '订阅更新',
        sideCtaText: '新文章送到你邮箱。',
        notFound: '没有找到这篇文章。',
        notFoundBack: '返回全部文章',
      },
      sections: {
        latest: { title: '最新文章', subtitle: '我们最近发布的内容：场景拆解、实操指南与产品记录。' },
        viewAll: '查看全部',
        news: { title: '产品动态', subtitle: '版本说明、功能更新，以及下个季度的计划。' },
        stories: { title: '场景实战', subtitle: '按卖家类型拆解：夜间来电、售后咨询，通常会怎么处理。' },
      },
      // 视频与实录（标题避开「精选」这种广告化说法）
      videos: {
        title: '视频与实录',
        subtitle: '产品演示与真实通话，几分钟看完。',
        viewAll: '查看全部',
        inCategoryTitle: '本分类视频',
        modalNote: '演示视频即将上线，播放器已就位。',
        close: '关闭',
        items: [
          {
            id: 'v-answer',
            cat: 'product',
            duration: '2:14',
            tag: '产品演示',
            title: 'AI 如何在 3 秒内接起一通美国来电。',
            desc: '从铃响到问候，通话记录已经同步在屏幕上。',
            date: '2026 年 10 月',
          },
          {
            id: 'v-afterhours',
            cat: 'usecase',
            duration: '3:40',
            tag: '通话实录',
            title: '一通真实的夜间来电 —— 从接起到工单生成。',
            desc: '完整闭环：接起、问清、生成工单、发出链接。',
            date: '2026 年 10 月',
          },
          {
            id: 'v-sms',
            cat: 'product',
            duration: '1:52',
            tag: '产品演示',
            title: '在顾客挂断之前，把短信链接发出去。',
            desc: '为什么一条跟进链接，比一句"稍后回电"更有效。',
            date: '2026 年 9 月',
          },
          {
            id: 'v-setup',
            cat: 'howto',
            duration: '4:05',
            tag: '上手演示',
            title: '10 分钟上线：号码、开场白，和你的第一条 FAQ。',
            desc: '从注册到"一通电话收成一张工单"的最短路径。',
            date: '2026 年 9 月',
          },
          {
            id: 'v-cost',
            cat: 'industry',
            duration: '2:48',
            tag: '数据拆解',
            title: '一通漏接的售后电话，到底亏多少？一次拆解。',
            desc: '这个数字从哪来，以及哪个假设对结果影响最大。',
            date: '2026 年 8 月',
          },
          {
            id: 'v-tone',
            cat: 'product',
            duration: '3:12',
            tag: '产品演示',
            title: '按品牌调 AI 的语气、语速与读音。',
            desc: '四个旋钮，不用重写一句脚本。',
            date: '2026 年 8 月',
          },
        ],
      },
      categoryTiles: [
        { icon: 'book', title: '快速上手', desc: '开通美国号码、接入团队、接起第一通电话。', count: 12 },
        { icon: 'headphones', title: '语音与接待', desc: 'AI 如何应答、如何转接，顾客实际听到的是什么。', count: 9 },
        { icon: 'clipboard', title: '工单与跟进', desc: '把一通电话变成工单、短信链接，以及一个闭环。', count: 7 },
        { icon: 'dollar', title: '价格与套餐', desc: '通话分钟、号码、增值项，以及计费是怎么算的。', count: 5 },
      ],
      subscribe: {
        title: '新文章第一时间送到你邮箱',
        subtitle: '有真实更新时一封邮件：场景实战、产品变动或行业观察。不发垃圾邮件。',
        placeholder: '你的邮箱',
        cta: '订阅',
        success: '已收到，请去邮箱确认。',
        error: '暂时无法订阅，请稍后再试。',
        invalidEmail: '邮箱格式不对，请检查一下。',
        tooMany: '提交次数太多，请稍后再试。',
      },
    },

    // ---------- 帮助中心 ----------
    // 结构参考《帮助中心 · 信息架构建议》：搜索优先 → 6 个任务型分类 → 常见问题 + 故障排查 → 联系支持；
    // 文章统一模板：目标 / 前置 / 步骤 / 确认 / 常见问题 / 相关文章。
    // 正文内容来自《AIkerr 客户开通与账户管理操作SOP》；SOP 里标【待确认】的商业政策，
    // 这里如实写「仍在确认中」，不编造已生效的规则。
    help: {
      hero: {
        eyebrow: '帮助中心',
        title: '您好，需要什么帮助？',
        subtitle: '查找开通指南、功能教程和常见问题。',
        searchPlaceholder: '搜索问题，例如：注册、号码、AI 配置',
        hotLabel: '热门：',
        // 每个词都验证过能命中文章（haystack = 标题/摘要/目标/前置/步骤/完成标志/FAQ）
        hot: ['注册', '号码', '知识库', '退款'],
      },
      // 首页第一屏：两条路径（走流程 / 查账户），先把新客户送进开通，而不是让他先逛主题
      quickStart: {
        title: '第一次使用 Bizsprite?',
        cards: [
          { key: 'guide', title: '开始使用', desc: '了解从开通到正式接听的完整流程。', cta: '查看开通指南' },
          { key: 'register', title: '注册与账户', desc: '注册、验证及账户设置的操作教程。', cta: '查看注册教程' },
        ],
      },
      hotTitle: '热门问题',
      noAnswer: {
        title: '没有找到答案?',
        desc: '联系我们的支持团队，获取进一步帮助。',
        note: '邮件由人工回复，一个工作日内处理；电话由 AI 接听，可以先听效果、直接提问。',
        email: 'support@bizsprite.com',
      },
      // 「服务边界与数据」原来的内容挪到这里：它不是操作教程，不该占首页中间
      rules: {
        title: '产品规则与安全',
        subtitle: '服务范围、数据处理与使用限制，购买和上线之前先说清楚。',
        items: [
          { icon: 'shield', title: 'AI 客服的服务范围', desc: '了解 AI 可处理的问题与人工介入方式。', text: 'AI 只回答资料范围内的问题。复杂售后、退款、投诉、定制需求会生成工单并转人工，不会自行承诺。' },
          { icon: 'lock', title: '数据管理与隐私说明', desc: '了解客户信息、通话记录及数据处理规则。', text: '通话录音、转写与客户信息都按实际产品政策管理；涉及合规问题会转专业团队确认。' },
          { icon: 'message', title: '短信与通话使用规则', desc: '了解短信发送、客户同意和使用限制。', text: '短信只在通话中或工单跟进时发送，内容需符合当地法规；通话分钟按套餐与实际用量计费，超出部分按量结算。' },
        ],
      },
      allTopics: '全部主题',
      // 区块大标题单独一个字段：allTopics 还被筛选按钮组复用（L4190/L4237），不能一起改
      allTopicsTitle: '按主题查找帮助',
      allTopicsSub: '六个主题，对应从开通到日常使用的六个阶段。',
      articlesTitle: '指南与答案',
      emptyTitle: '没有匹配的文章',
      emptyHint: '换个关键词，或者从下面的主题进入。',
      // 七个分类按「用户处在哪个阶段」排，不按功能模块排。
      // 「短信与工单」暂时不开：现在还没有可写的教程，空分类比没有分类更糟。
      categories: [
        { key: 'start', icon: 'rocket', title: '快速入门', desc: '这套服务是什么，开通前要准备什么。' },
        { key: 'account', icon: 'user', title: '账户与开通', desc: '注册、邮箱验证、首次登录，以及为什么需要第三方账户。' },
        { key: 'number', icon: 'phone', title: '号码与通话', desc: '号码开通、AI 接听、通话记录，以及上线测试。' },
        { key: 'ai', icon: 'mic', title: 'AI 客服配置', desc: 'Agent、知识库与接待规则。' },
        { key: 'billing', icon: 'card', title: '账单与费用', desc: '套餐、充值、计费，以及取消与退款。' },
        { key: 'troubleshoot', icon: 'tool', title: '故障排查', desc: '常见错误及解决方法。' },
      ],
      articles: [
        {
          id: 'what-is-aikerr',
          cat: 'start',
          title: '你买到的到底是什么',
          read: '3 分钟',
          summary: '一个北美本地号码，加上一个会接电话的 AI。短信、工单、通话记录，都挂在这一通电话上。',
          goal: '能用一句话说清这套服务替你的团队干了什么。',
          before: ['你在做北美市场，电话会因为时差或人手接不上。'],
          steps: [
            { t: '一个本地号码，成为你的门面', d: '顾客拨的是一个看起来像本地公司的美国号码，而不是境外客服中心。' },
            { t: 'AI 用你自己的资料回答', d: '商品、物流、售后问题，都依据你上传的知识库回答；资料里没有的，它会转人工，而不是编一个。' },
            { t: '通话结束时会留下可跟进的东西', d: '通话中短信发出链接；没解决的问题变成工单，附带录音、转写和摘要。' },
          ],
          confirm: '你能指着号码、AI 和工单队列，分别说出谁负责。',
          related: ['two-platforms', 'prepare', 'upload-kb'],
        },
        {
          id: 'two-platforms',
          cat: 'start',
          title: '为什么点「立即开通」会跳到另一个网站',
          read: '3 分钟',
          summary: 'Bizsprite 提供 AI 能力，OneSuite Business 提供美国号码和通信服务。两个账号，同一套服务。',
          goal: '明白开通为什么跨两个平台，以及哪件事该去哪个后台办。',
          before: ['你还没在任何地方注册过。先看这篇，避免注册出两个账号。'],
          steps: [
            { t: 'Bizsprite 负责 AI', d: '创建 AI Agent、上传商品与 FAQ 资料、写开场白与转人工规则。' },
            { t: 'OneSuite Business 负责号码与线路', d: '美国号码开通、通话分钟、短信、账单、余额与自动充值。' },
            { t: '跳转是正常路径', d: '在本站点「立即开通」，会打开 OneSuite Business 注册页，让号码和通信账户建在同一个企业主体下。' },
          ],
          confirm: '你知道账单问题找 OneSuite，AI 相关问题找我们。',
          faq: [
            { q: '必须注册 OneSuite Business 吗？', a: '必须。美国号码和通话分钟都在那边开通，它是同一套服务的通信侧，不是另一个产品。' },
            { q: '两个网站都有注册入口，该用哪个？', a: '从本站进，跟着跳转走。从第二个入口重复注册可能产生重复账号，后面的绑定会很麻烦。' },
          ],
          related: ['activate-jump', 'register', 'first-login'],
        },
        {
          id: 'prepare',
          cat: 'start',
          title: '开通前要准备好的三样东西',
          read: '4 分钟',
          summary: '企业资料、业务资料、一个付款方式。提前半小时凑齐，能省掉一整天的来回。',
          goal: '东西都在手边，开通不会做到一半卡住。',
          steps: [
            { t: '企业资料', d: '注册用的公司名称、一个长期有效的企业邮箱、联系人，以及企业地址。要与实际经营主体一致——审核是拿这些去对的。', shot: '注册表单——公司名称 / 邮箱 / 地址' },
            { t: '商品与政策资料', d: '商品介绍、FAQ、物流政策、售后政策、退款规则。这些是 AI 回答的依据，朴素的原文比精美的宣传册更有用。' },
            { t: '一个付款方式', d: '信用卡、支付宝或微信支付，以账单页实际提供的为准。用于余额充值。' },
          ],
          confirm: '填注册表单时不用离开座位去翻资料。',
          related: ['register', 'upload-kb', 'recharge'],
        },
        {
          id: 'activate-jump',
          cat: 'account',
          title: '点「立即开通」之后会发生什么',
          read: '3 分钟',
          summary: '新标签页打开 OneSuite Business。接着是：注册 → 邮箱验证 → 选套餐 → 付款，最后你会拿到两个账号。',
          goal: '知道接下来会发生什么，跳转就不会像是「离开了这个产品」。',
          before: ['企业资料、企业邮箱和付款方式都已备好。'],
          steps: [
            { t: '打开一个新标签页', d: 'OneSuite Business 注册页在新标签页里打开，本站仍留在后面。', shot: 'OneSuite Business 注册页' },
            { t: '注册并验证', d: '填写企业信息表单，然后输入发到邮箱的验证码。' },
            { t: '选套餐并付款', d: '选择套餐和付款周期，完成支付。' },
            { t: '系统创建两个账号', d: '一个是 OneSuite Business 账号（管号码与账单），一个是 AI 平台账号（管 Agent）。登录信息会发到你的邮箱。' },
          ],
          confirm: '邮箱里同时拿到两个平台的登录信息。',
          related: ['register', 'first-login', 'two-platforms'],
        },
        {
          id: 'register',
          cat: 'account',
          title: '注册 OneSuite Business 企业账户',
          read: '4 分钟',
          summary: '按照以下步骤创建 OneSuite Business 企业账户，完成所需信息填写与账户验证，为后续服务配置做好准备。',
          goal: '创建用于申请美国号码的企业账户。',
          before: ['公司名称、联系人、企业邮箱与企业地址。'],
          steps: [
            { t: '打开注册表单', d: '在跳转后的页面点击「Sign Up / Register」。', shot: 'OneSuite Business——Sign Up' },
            { t: '填写企业信息', d: '公司名称、联系人姓名、企业邮箱、企业地址，以及表单要求的其他认证信息。', shot: '企业信息表单' },
            { t: '完成邮箱验证', d: '系统会把验证码发到你注册的邮箱，输入即完成。', shot: '邮箱验证码页面' },
          ],
          confirm: '账户后台能打开，并显示你的企业信息。',
          faq: [
            { q: '验证码有效期多久？', a: '15 分钟。过期就重新获取一次。' },
            { q: '可以用 Gmail 这类个人邮箱吗？', a: '一般可以，但建议用企业域名邮箱——号码在用多久，这个邮箱就要能收信多久。' },
          ],
          related: ['email-code', 'first-login', 'recharge'],
        },
        {
          id: 'email-code',
          cat: 'account',
          title: '收不到邮箱验证码',
          read: '3 分钟',
          summary: '十次里有九次是垃圾邮件或企业邮件网关拦了。按顺序查这四步。',
          goal: '不靠注册第二个账号，也能过掉邮箱验证。',
          steps: [
            { t: '先看垃圾邮件和「推广」标签', d: '验证码是自动发件人发出的，很容易被企业邮件网关误判。' },
            { t: '核对邮箱有没有打错', d: '错一个字符，验证码就发去了你打不开的地址，而表单不会提醒你。' },
            { t: '只重发一次，然后等五分钟', d: '反复点重发可能触发发件方限流，反而让后面每一封都更慢。' },
            { t: '还是收不到——换邮箱，或者找我们', d: '换一个企业邮箱试试，或者把公司名称发到 support@bizsprite.com，我们从后台帮你核。', shot: '邮箱里收到的验证邮件' },
          ],
          confirm: '后台能打开，就说明邮箱已验证通过。',
          related: ['register', 'first-login'],
        },
        {
          id: 'first-login',
          cat: 'account',
          title: '首次登录，先核对这四项',
          read: '3 分钟',
          summary: '登录、改初始密码，然后确认账号、AI 模块、企业信息和套餐都在该在的位置。',
          goal: '从一个验证过的、配置正确的账号开始，而不是在通话中途才发现问题。',
          before: ['邮箱里收到的账号密码。'],
          steps: [
            { t: '登录并修改密码', d: '用邮件里的初始密码登录，设置新密码，再重新登录确认一次。' },
            { t: '确认账号已创建', d: '后台能打开，并且显示你的公司名称。', shot: 'OneSuite 后台首页' },
            { t: '确认 AI 模块正常显示', d: '控制台里能看到 AI 服务模块，而不是空白或报错状态。', shot: '控制台里的 AI 服务模块' },
            { t: '确认企业信息与套餐', d: '企业信息和已购套餐都要能看到。缺任何一项，先联系我们再往下做。' },
          ],
          confirm: '四项都对。不对就停下来找支持——在一个有问题的账号上配 AI，比等一天更费时间。',
          related: ['register', 'create-agent', 'recharge'],
        },
        {
          id: 'recharge',
          cat: 'billing',
          title: '如何充值',
          read: '3 分钟',
          summary: '账单 → 充值 → 金额与付款方式。预付余额就是让线路一直有人接的那笔钱。',
          goal: '给账户加上预付余额，保证服务不中断。',
          before: ['已登录 OneSuite Business 后台。', '手边有可用的付款方式。'],
          steps: [
            { t: '进入账单页', d: '在后台打开「Billing 账单」。', shot: '后台——Billing 账单' },
            { t: '点击充值', d: '选择「RECHARGE / 充值」，开始一次性充值。', shot: 'Billing——RECHARGE 充值' },
            { t: '选择金额与付款方式', d: '确认充值金额，选择支付方式，完成付款。', shot: '金额与付款方式' },
          ],
          confirm: '账单页余额增加，并且这笔充值出现在交易记录里。',
          related: ['auto-recharge', 'billing-read', 'refund'],
        },
        {
          id: 'auto-recharge',
          cat: 'billing',
          title: '设置自动充值，别让通话因为余额停下',
          read: '4 分钟',
          summary: '账单 → Auto-Recharge：勾选开启、设置金额和触发阈值、更新付款方式。之后余额低了会自己补上。',
          goal: '不让余额不足在半夜变成漏接。',
          before: ['已登录 OneSuite Business 后台。'],
          steps: [
            { t: '在账单页找到自动充值', d: '「Billing 账单」→「Auto-Recharge」。', shot: 'Billing——Auto-Recharge' },
            { t: '开启自动充值', d: '勾选「Auto-Recharge prepaid credit」。' },
            { t: '设置金额与触发阈值', d: '金额是每次自动补多少；阈值是余额低于多少时触发。阈值要留够一个忙碌周末的余量。', shot: '金额与阈值设置' },
            { t: '同意条款并更新付款方式', d: '勾选同意服务条款，更新自动付款的信用卡信息并确认。', shot: '付款方式确认' },
          ],
          confirm: '自动充值显示为已启用，余额低于阈值时会自动补充。',
          faq: [
            { q: '阈值该设多少？', a: '至少覆盖几天的正常话量。若余额不足将导致漏接，建议此处留有余量。' },
          ],
          related: ['recharge', 'billing-read'],
        },
        {
          id: 'billing-read',
          cat: 'billing',
          title: '看懂账单：哪些按量、哪些固定',
          read: '4 分钟',
          summary: 'AI 接听和 AI 对话不限量；通话分钟和短信按量计费；号码是固定月费。',
          goal: '知道哪些费用会随话量变化，哪些不会。',
          steps: [
            { t: '固定项', d: '号码和月订阅是固定的月度金额，不随话量变化。' },
            { t: '按量项', d: '通话分钟、短信，以及额外号码或分机，按实际使用计费。每一项单价都公开在定价页上。' },
            { t: '余额与账单是两回事', d: '预付余额是服务实时扣减的那笔钱；账单是扣减的记录。充值不是消费，是给余额补钱。' },
          ],
          confirm: '只看账单页，你也能判断总额变化是来自话量还是来自套餐变更。',
          faq: [
            { q: '可以中途换套餐吗？', a: '可以。升级立即生效，本周期剩余部分按比例折算；降级从下个周期生效，已经付过的部分继续用完。' },
          ],
          related: ['recharge', 'auto-recharge', 'cancel'],
        },
        {
          id: 'create-agent',
          cat: 'ai',
          title: '创建接电话的 AI Agent',
          read: '3 分钟',
          summary: '一条线一个 Agent。起个名字、写清业务、保存——剩下的等第一通测试电话之后再调。',
          goal: '有一个可以被号码指向的 AI Agent。',
          before: ['你能登录 AI 平台。'],
          steps: [
            { t: '进入 AI Assistants', d: '在 AI 平台打开「AI Assistants」。', shot: '电话设置——欢迎语与声音', shotImg: '/help/create-agent-0.png' },
            { t: '新建 Agent', d: '起一个团队一眼能认出的名字，通常是品牌加线路。' },
            { t: '填写基础信息', d: '业务卖什么、谁会打来、哪些事允许 Agent 自己决定。' },
            { t: '保存', d: '确认并保存。Agent 建好了，但它还什么都不知道——下一步是知识库。' },
          ],
          confirm: 'Agent 出现在 AI Assistants 列表里，配置号码时能选中它。',
          related: ['upload-kb', 'rules', 'first-login'],
        },
        {
          id: 'upload-kb',
          cat: 'ai',
          title: '上传 AI 回答所依据的资料',
          read: '5 分钟',
          summary: '两个库：商品库和 FAQ 库。按模板上传，然后把两个库都关联到 Agent。你没传的，它不会编。',
          goal: '让 AI 依据你的真实资料回答，而不是靠通用常识。',
          before: ['商品介绍、FAQ、物流政策、售后政策、退款规则。'],
          steps: [
            { t: '上传商品知识库', d: '按后台模板填。型号、兼容性、尺寸与变体，是顾客问得最多的字段。', shot: '知识库——商品库模板' },
            { t: '上传 FAQ 知识库', d: '「问题—答案」成对填写。按顾客实际的问法写，而不是按政策文件的措辞写。' },
            { t: '把两个库关联到 Agent', d: '只上传还不够——必须把库关联到 Agent，否则它回答时手里是空的。', shot: '把知识库关联到 Agent' },
            { t: '用三个真实问题试一次', d: '用这个月顾客真的问过的问题，其中至少有一个你自己知道很刁钻。' },
          ],
          confirm: 'AI 用你的资料回答了那三个问题；超出资料的，它会说要去跟团队确认。',
          faq: [
            { q: 'AI 不知道答案时会怎样？', a: '它应该转人工，而不是临场发挥。如果它编了答案，那就是知识库有缺口——补资料，或者加一条规则把这个意图转给人工。' },
          ],
          related: ['create-agent', 'rules', 'checklist'],
        },
        {
          id: 'rules',
          cat: 'ai',
          title: '服务规则：开场白、服务时间与转人工',
          read: '4 分钟',
          summary: '开场白怎么说、AI 覆盖哪个时间段、哪些意图必须落到人手上。',
          goal: '想清楚哪些事 AI 自己处理，哪些事一定交给你的团队。',
          steps: [
            { t: '写开场白', d: '控制在 12 秒以内：你是谁、能帮他做什么、接下来会发生什么。别用「您来电对我们很重要」。' },
            { t: '设置服务时间', d: '服务时间内可以先响团队，响够次数再落到 AI；服务时间外由 AI 直接接起。' },
            { t: '定义转人工规则', d: '列出必须转人工的意图：投诉、涉及法律或安全边界的、以及不允许 AI 自行决定的任何事。', shot: '规则——转人工配置' },
          ],
          confirm: '你能当面说出三件 AI 永远不会自己决定的事。',
          faq: [
            { q: '规则模板已经定稿了吗？', a: '配置项已经就位，但部分政策细节仍在确认中。把你的场景告诉我们，上线前我们会针对你的账号确认哪些规则适用。' },
          ],
          related: ['create-agent', 'upload-kb', 'checklist'],
        },
        {
          id: 'checklist',
          cat: 'number',
          title: '上线前检查清单',
          read: '6 分钟',
          summary: '四块：AI 配置、一通真实电话、短信、回答质量。全部打勾，才让真实流量打进来。',
          goal: '在顾客之前，先把配置缺口找出来。',
          steps: [
            { t: 'AI 配置', d: 'Agent 已创建 · 知识库已上传 · 知识库已关联 Agent · 开场白已配置 · 工作时间已配置 · 转人工规则已配置。' },
            { t: '一通真实电话', d: '用外部手机打这个号码。电话接通 · AI 接起 · 商品问题回答正确 · 转人工流程可用。', shot: '测试通话记录' },
            { t: '短信', d: '如果套餐含短信：通话中发一条链接，确认手机能收到。', shot: '手机上收到的短信' },
            { t: '回答质量', d: '商品问题准确 · FAQ 准确 · 售后按政策处理 · 未知问题会转人工而不是编答案。' },
          ],
          confirm: '上面每一条都打勾了。测试电话打不通不花钱，顾客电话打不通丢的是顾客。',
          related: ['go-live', 'upload-kb', 'rules'],
        },
        {
          id: 'go-live',
          cat: 'number',
          title: '正式上线',
          read: '3 分钟',
          summary: '清单清空之后，Agent 就可以接真实来电了。先挂一个触点，不要一次全铺开。',
          goal: '让号码出现在真实顾客面前，但不搞「一刀切」式上线。',
          before: ['上线前检查清单的每一项都已打勾。'],
          steps: [
            { t: '先只挂一个触点', d: '官网的联系页面最容易加、也最容易撤。包装和广告文案留到后面。' },
            { t: '盯住第一天', d: '读通话转写。第一周要修的基本都是知识库缺口，不是模型问题。' },
            { t: '再逐步铺开', d: '第一天看起来干净之后，再把号码加到包装、商品页和售后邮箱签名里。' },
          ],
          confirm: '号码至少在一个渠道上线，并且每天都有人在读通话转写。',
          related: ['checklist', 'billing-read', 'cancel'],
        },
        {
          id: 'cancel',
          cat: 'billing',
          title: '取消服务',
          read: '4 分钟',
          summary: '后台 → Company 公司 → Cancel Account 取消账户。服务在下一个账单日停止，不是立刻——在那之前账号照常可用。',
          goal: '干净地停掉服务，并且知道停掉之前还会产生什么费用。',
          before: ['已登录 OneSuite Business 后台。'],
          steps: [
            { t: '进入 Company 页面', d: '在后台左侧目录点击「Company 公司」。', shot: '后台——Company 公司' },
            { t: '找到取消账户', d: '在页面左下角找到「Cancel Account 取消账户」。', shot: 'Company——Cancel Account' },
            { t: '阅读取消说明', d: '说明里会写清服务何时停止、余额怎么处理、历史记录保存多久。' },
            { t: '确认取消', d: '勾选「我了解取消账户的影响」，再继续提交申请。', shot: '取消确认页' },
          ],
          confirm: '取消申请已记录，并显示了服务停止日期。',
          faq: [
            { q: '会立刻停吗？', a: '不会。服务在下一个账单日停止；在那之前账号正常可用。' },
            { q: '这期间还能用吗？', a: '能。但这段时间产生的费用照常计算，包括外拨电话、AI 超额使用和其他套餐外费用，都从预付余额里扣。' },
          ],
          related: ['refund', 'billing-read'],
        },
        {
          id: 'refund',
          cat: 'billing',
          title: '余额会怎么处理',
          read: '3 分钟',
          summary: '未使用的预付余额会自动退回，通常在服务停止后 7 个工作日内。细则仍在确认中。',
          goal: '知道能退回多少、什么时候到账。',
          steps: [
            { t: '余额退回原付款方式', d: '服务停止时如果还有未使用的预付余额，平台会自动处理退款，通常 7 个工作日内。', shot: '退款记录' },
            { t: '停用前产生的费用照常从余额扣', d: '在服务真正停止之前，外拨电话、AI 超额使用和其他套餐外费用会照常扣减。' },
            { t: '退款细则仍在确认中', d: '具体条件、金额计算方式、是否扣除号码费用、以及到账周期都还在确认。定下来我们会更新在这里；需要的话也可以按你的账号书面确认。' },
          ],
          confirm: '你知道预期到账时间，也知道哪些部分还有待确认。',
          faq: [
            { q: '号码费用能退吗？', a: '尚未确认。我们不会猜——问我们，按你的账号给准确答复。' },
            { q: '退款多久能到？', a: '平台的目标是服务停止后 7 个工作日。银行或发卡行可能再加几天。' },
          ],
          related: ['cancel', 'billing-read'],
        },
      ],
      faqTitle: '热门问题',
      faqSubtitle: '客户问得最多的几个问题，点开看短答案。',
      faq: [
        { q: '如何注册并开通 Bizsprite？', a: '从本站「立即开通」进入 OneSuite Business 完成注册与邮箱验证，选套餐并充值后，回到 Bizsprite 后台创建 AI Agent。完整步骤见「开始使用」。' },
        { q: '必须注册 OneSuite Business 吗？', a: '必须。美国号码和通话分钟都在那边开通，它是同一套服务的通信侧。详见「为什么点「立即开通」会跳到另一个网站」。' },
        { q: '如何配置 AI 接待内容？', a: '在后台创建 Agent，上传企业资料、商品与售后 FAQ，再设好开场白与服务时间。AI 只按你上传的资料回答。' },
        { q: 'AI 无法回答客户问题怎么办？', a: '它会转人工并生成工单，不会自行承诺。把缺失的资料补进知识库，同类问题下次就能答上。' },
      ],
      troubleTitle: '故障排查指引',
      troubleSubtitle: '查看常见使用问题及对应的处理建议。',
      trouble: [
        { icon: 'mail', title: '收不到邮箱验证码', desc: '先查垃圾邮件，再重新发送；换了邮箱还收不到，按这篇逐项排查。', to: 'email-code' },
        { icon: 'phone', title: '电话打进来没人接', desc: '先看号码状态、开场白是否播得出，以及有没有一条转移规则把电话送去了别处。', to: 'checklist' },
        { icon: 'message', title: '短信链接收不到', desc: '短信不保证 100% 到达手机。先查模板、手机端，以及 AI 当时告诉顾客会发什么。', to: 'checklist' },
        { icon: 'mic', title: 'AI 答错了', desc: '基本都是知识库缺口。找到那条转写，把本该用到的资料补上或改对。', to: 'upload-kb' },
        { icon: 'card', title: '月中余额就没了', desc: '把自动充值打开，让余额在变成漏接之前自己补上。', to: 'auto-recharge' },
        { icon: 'user', title: '登录不进去', desc: '密码、邮箱，或者账号主体选错，都会停在登录页。先核对这四项。', to: 'first-login' },
      ],
      article: {
        home: '帮助中心',
        updatedLabel: '更新于',
        updatedOn: '2026-10-10',
        tocTitle: '本页目录',
        goalLabel: '目标',
        beforeLabel: '前置条件',
        stepsLabel: '操作步骤',
        confirmLabel: '完成标志',
        faqLabel: '常见问题',
        relatedLabel: '相关操作指南',
        helpfulLabel: '本文是否解决了您的问题？',
        helpfulYes: '有用',
        helpfulNo: '不太有用',
        helpfulThanks: '谢谢，记下了。还是没解决？把当时的情况告诉我们，我们会补进这篇里。',
        helpfulReasonLabel: '哪里没解决？',
        helpfulReasons: ['截图对不上实际界面', '步骤不完整', '和我的情况不一样', '没找到我要的答案'],
        helpfulMailCta: '把这处问题发给我们',
        shotLabel: '截图占位',
        backToList: '返回帮助中心',
        backToCat: '返回',
        stillStuck: '还是没解决？',
        stillStuckCta: '联系我们',
        notFound: '这篇文章不存在。',
      },
      contact: {
        eyebrow: '还是没解决？',
        title: '需要进一步协助？联系我们的技术团队',
        subtitle: '如果现有文档未能解答您的问题，可以通过邮件或电话获取支持。',
        emailLabel: '邮件联系',
        emailNote: '适合贴日志、截图或账号信息，一个工作日内回复。',
        email: 'support@bizsprite.com',
        demoLabel: '电话',
        demoNote: '想先听听产品，直接拨这个号码，用你自己的问题问它。',
        demoValue: '+1 (408) 877-9998',
      },
    },
    // ---------- 线索表单（两个页面共用） ----------
    // 「联系我们」页的表单字段。原「预约演示」页已随功能下架移除，字段定义保留给联系我们页。
    lead: {
      form: {
        name: '姓名',
        namePh: '陈晓',
        email: '工作邮箱',
        emailPh: 'you@company.com',
        emailHint: '使用 Gmail 等个人邮箱也可以，很多卖家都是这样注册的。',
        phone: '手机或 WhatsApp',
        phonePh: '+86 138 0000 0000',
        phoneHint: '只用于这次沟通。',
        company: '公司或店铺名称',
        companyPh: '例：北风户外',
        platforms: '你在哪些平台卖货？',
        platformsHint: '可多选。',
        platformOptions: ['亚马逊', 'Shopify', 'TikTok Shop', 'eBay', '沃尔玛', 'Temu', '自建站', '还没开始卖'],
        volume: '客服接待量/电话',
        volumeOptions: ['100 通以内', '100 – 500 通', '500 – 2,000 通', '2,000 通以上', '还不确定'],
        message: '还有什么想告诉我们的？',
        messagePh: '例：晚上 9 点到次日 6 点的来电基本无人接听，退货咨询平均三天后才回复。',
        optional: '选填',
        consentPre: '我同意 Bizsprite 就本次咨询通过邮件或电话联系我，并已阅读',
        consentLink: '隐私政策',
        consentPost: '。',
        submit: '提交咨询',
        sending: '提交中…',
        requiredNote: '带 * 的是必填项。',
        errName: '请填写你的姓名。',
        errEmail: '请填写有效的邮箱地址。',
        errPhone: '请填写能联系到你的号码。',
        errCompany: '请填写公司或店铺名称。',
        errPick: '请选择一项。',
        errConsent: '提交前请先勾选同意。',
        errSubmit: '提交没有成功，请重试，或直接发邮件到 support@bizsprite.com。',
        errTooMany: '提交次数过多，请稍后再试，或直接发邮件到 support@bizsprite.com。',
      },
      aside: {
        email: 'support@bizsprite.com',
        phone: '+1 (408) 877-9998',
        trustTitle: '提交之前',
        trust: [
          '一个工作日内回复，中英文都可以。',
          '你的信息仅用于回复本次咨询，不会对外提供，也不会加入群发名单。',
          '在你决定开通之前，不需要转号，也不签任何合约。',
        ],
      },
      success: {
        title: '已收到你的咨询',
        contactText: '我们会在一个工作日内回复到你填写的邮箱。如需尽快沟通，也可以直接打电话过来，当场确认问题。',
        again: '再提交一次',
      },
    },

    // ---------- 联系我们 ----------
    // 注意：key 叫 contactPage，不能叫 contact —— 顶层已有一个 contact 块
    // （首页底部 CTA 带在用），重名会被后者覆盖，首页那条带子会变空。
    contactPage: {
      eyebrow: '联系我们',
      title: '了解适合您业务的 AI 客户服务方案',
      subtitle: '告诉我们您的业务场景、服务需求与预期目标。我们的团队将与您沟通产品能力、适用方式及相关服务信息，协助您评估合适的方案。',
      topicLabel: '这次想聊什么？',
      topicOptions: ['产品咨询', '定价与用量', '技术支持', '合作与渠道', '其他'],
      formNote: '我们会在一个工作日内，回复到你填写的邮箱，中英文都可以。',
      channelsTitle: '与我们的团队取得联系',
      channels: [
        { icon: 'mail', label: '邮件', value: 'support@bizsprite.com', href: 'mailto:support@bizsprite.com', note: '一个工作日内回复。' },
        { icon: 'phone', label: '电话', value: '+1 (408) 877-9998', href: 'tel:+14088779998', note: '美国本地号码，可直接致电沟通。' },
        { icon: 'pin', label: '办公地址', value: '168 W Pomona Ave, Monrovia, CA 91016', href: '', note: '如在本地区，欢迎到访面谈。' },
      ],
      routingTitle: '哪件事走哪个渠道',
      routing: [
        { label: '功能使用与故障反馈', via: '发邮件', note: '附上店铺名称与截图，可加快处理。' },
        { label: '报价、话量、年付条款', via: '填写表单', note: '选择「定价与用量」，将直接转交销售。' },
        { label: '合作、代理、渠道', via: '发邮件', note: '邮件标题注明「合作」，便于分流。' },
        { label: '举报滥用或号码误拨', via: '发邮件', note: '邮件标题注明「举报」，我们将优先处理。' },
      ],
    },

  },
};
